import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "countedField",
    "counter",
    "copySource",
    "imagePreview",
    "imageLabel",
    "freshnessPanel",
    "freshnessMessage",
    "navLink",
    "featureSelect",
    "packageModeToggle",
    "packageCard",
    "extraOptionRow",
    "keywordInput",
    "keywordHiddenList",
    "keywordChipList",
    "keywordCount",
    "keywordCharCount",
    "faqList",
    "faqTemplate",
    "faqCount",
    "workRequestList",
    "workRequestTemplate",
    "workspaceTab",
    "workspacePanel",
    "workspaceLayout",
    "sectionNav",
    "comparisonReportShell",
    "comparisonReportImprovedPreview",
    "comparisonReportPaneToggle",
    "comparisonReportAgent",
    "comparisonReportAgentToggle",
    "comparisonReportAgentLauncher"
  ]
  static values = {
    currentUpdatedAt: String,
    freshnessUrl: String,
    activeTab: { type: String, default: "service" }
  }

  connect() {
    this.pollTimer = null
    this.activeComparisonPollTimer = null
    this.activeComparisonElapsedTimer = null
    this.activeComparisonProgressRunId = null
    this.activeComparisonProgressStartedAt = null
    this.activeComparisonProgressButton = null
    this.activeComparisonProgressOriginalText = null
    this.activeComparisonRequestController = null
    this.activeComparisonPollInFlight = false
    this.comparisonRowPollTimers = new Map()
    this.comparisonRowPollInFlight = new Set()
    this.comparisonReloadScheduled = false
    this.comparisonSaveInFlight = false
    this.comparisonSaveCancelled = false
    this.comparisonSaveWatchTimer = null
    this.comparisonSaveWatchResolve = null
    this.isPollingFreshness = false
    this.hasExternalUpdate = false
    this.pollFreshness = this.pollFreshness.bind(this)
    this.handleAgentMessageComplete = this.handleAgentMessageComplete.bind(this)
    this.closeFeatureSelectOnOutsideClick = this.closeFeatureSelectOnOutsideClick.bind(this)
    this.closeFeatureSelectOnEscape = this.closeFeatureSelectOnEscape.bind(this)
    this.closeComparisonSelectOnOutsideClick = this.closeComparisonSelectOnOutsideClick.bind(this)
    this.closeComparisonSelectOnEscape = this.closeComparisonSelectOnEscape.bind(this)
    this.handleComparisonReportAgentClick = this.handleComparisonReportAgentClick.bind(this)
    this.handleComparisonPreviewRequest = this.handleComparisonPreviewRequest.bind(this)
    this.handleComparisonDetailImagesRequest = this.handleComparisonDetailImagesRequest.bind(this)

    this.countedFieldTargets.forEach((field) => this.updateCounterFor(field))
    this.updateFaqCount()
    this.updateWorkRequestPositions()
    this.updateWorkRequestOptionVisibility()
    this.applyPackageModeState()
    this.updateKeywordEditorState()
    this.applyWorkspaceTab()
    window.addEventListener("klavy-agent:message-complete", this.handleAgentMessageComplete)
    document.addEventListener("click", this.closeFeatureSelectOnOutsideClick)
    document.addEventListener("keydown", this.closeFeatureSelectOnEscape)
    document.addEventListener("click", this.closeComparisonSelectOnOutsideClick)
    document.addEventListener("keydown", this.closeComparisonSelectOnEscape)
    window.addEventListener("kmong-comparison-preview-request", this.handleComparisonPreviewRequest)
    window.addEventListener("kmong-comparison-detail-images-request", this.handleComparisonDetailImagesRequest)
    this.startFreshnessPolling()
    this.resumeActiveComparisonProgress()
    this.resumeComparisonRowProgress()
    this.restoreComparisonReportPanelState()
    this.syncComparisonReportPaneControls()
    this.attachComparisonReportAgentCloseListener()
    this.syncComparisonReportAgentControls()
  }

  disconnect() {
    window.removeEventListener("klavy-agent:message-complete", this.handleAgentMessageComplete)
    document.removeEventListener("click", this.closeFeatureSelectOnOutsideClick)
    document.removeEventListener("keydown", this.closeFeatureSelectOnEscape)
    document.removeEventListener("click", this.closeComparisonSelectOnOutsideClick)
    document.removeEventListener("keydown", this.closeComparisonSelectOnEscape)
    window.removeEventListener("kmong-comparison-preview-request", this.handleComparisonPreviewRequest)
    window.removeEventListener("kmong-comparison-detail-images-request", this.handleComparisonDetailImagesRequest)
    this.detachComparisonReportAgentCloseListener()
    this.stopSectionObserver()
    this.stopFreshnessPolling()
    this.stopActiveComparisonProgress({ restoreButton: false })
    this.stopComparisonRowPollTimers()
    this.stopComparisonSaveWatch()
  }

  updateCount(event) {
    this.updateCounterFor(event.currentTarget)
  }

  toggleComparisonReportPane(event) {
    event.preventDefault()

    const pane = event.currentTarget?.dataset?.comparisonReportPane
    if (!this.hasComparisonReportShellTarget || !this.comparisonReportPaneKeys().includes(pane)) return

    const shell = this.comparisonReportShellTarget
    const datasetKey = this.comparisonReportPaneDatasetKey(pane)
    const collapsed = shell.dataset[datasetKey] === "true"

    shell.dataset[datasetKey] = collapsed ? "false" : "true"
    this.persistComparisonReportPanelState()

    this.syncComparisonReportPaneControls()
  }

  syncComparisonReportPaneControls() {
    if (!this.hasComparisonReportShellTarget) return

    this.syncComparisonReportPaneLayout()

    if (!this.hasComparisonReportPaneToggleTarget) return

    const shell = this.comparisonReportShellTarget
    this.comparisonReportPaneToggleTargets.forEach((button) => {
      const pane = button.dataset.comparisonReportPane
      const collapsed = shell.dataset[this.comparisonReportPaneDatasetKey(pane)] === "true"
      const label = collapsed ? button.dataset.expandLabel : button.dataset.collapseLabel
      button.setAttribute("aria-pressed", collapsed ? "false" : "true")
      button.setAttribute("aria-label", label)
      button.setAttribute("title", label)
      if (!button.hasAttribute("data-icon-button")) button.textContent = label
    })
  }

  syncComparisonReportPaneLayout() {
    if (!this.hasComparisonReportShellTarget) return

    const visibleCount = this.visibleComparisonReportPaneCount()
    this.comparisonReportShellTarget.style.setProperty("--kcr2-visible-pane-count", String(Math.max(1, visibleCount)))
    this.comparisonReportShellTarget.dataset.visiblePaneCount = String(visibleCount)
  }

  comparisonReportPaneKeys() {
    return ["preview", "improved", "report"]
  }

  comparisonReportPaneDatasetKey(pane) {
    return `${pane}Collapsed`
  }

  visibleComparisonReportPaneCount() {
    if (!this.hasComparisonReportShellTarget) return 0

    return this.comparisonReportPaneKeys().filter((pane) => {
      return this.comparisonReportShellTarget.dataset[this.comparisonReportPaneDatasetKey(pane)] !== "true"
    }).length
  }

  comparisonReportPanelStateKey() {
    if (!this.hasComparisonReportShellTarget) return null
    return this.comparisonReportShellTarget.dataset.comparisonReportStateKey || null
  }

  restoreComparisonReportPanelState() {
    const state = this.readComparisonReportPanelState()
    if (!state || !this.hasComparisonReportShellTarget) return

    const shell = this.comparisonReportShellTarget
    this.comparisonReportPaneKeys().forEach((pane) => {
      const value = state[`${pane}Collapsed`]
      if (value === true || value === false) {
        shell.dataset[this.comparisonReportPaneDatasetKey(pane)] = value ? "true" : "false"
      }
    })

    if (this.hasComparisonReportAgentTarget && (state.agentOpen === true || state.agentOpen === false)) {
      this.comparisonReportAgentTarget.dataset.open = state.agentOpen ? "true" : "false"
      if (state.agentOpen) window.setTimeout(() => this.openEmbeddedKlavyAgent(), 0)
    }
  }

  persistComparisonReportPanelState() {
    const key = this.comparisonReportPanelStateKey()
    if (!key || !this.hasComparisonReportShellTarget) return

    const shell = this.comparisonReportShellTarget
    const state = this.comparisonReportPaneKeys().reduce((memo, pane) => {
      memo[`${pane}Collapsed`] = shell.dataset[this.comparisonReportPaneDatasetKey(pane)] === "true"
      return memo
    }, {})
    if (this.hasComparisonReportAgentTarget) {
      state.agentOpen = this.comparisonReportAgentTarget.dataset.open === "true"
    }

    try {
      window.localStorage?.setItem(key, JSON.stringify(state))
    } catch (_error) {
      // Ignore storage failures so panel controls keep working in private or restricted contexts.
    }
  }

  readComparisonReportPanelState() {
    const key = this.comparisonReportPanelStateKey()
    if (!key) return null

    try {
      const raw = window.localStorage?.getItem(key)
      return raw ? JSON.parse(raw) : null
    } catch (_error) {
      return null
    }
  }

  toggleComparisonReportAgent(event) {
    event.preventDefault()

    if (!this.hasComparisonReportAgentTarget) return

    const agent = this.comparisonReportAgentTarget
    if (agent.dataset.open === "true") {
      this.closeComparisonReportAgent(event)
    } else {
      this.openComparisonReportAgent(event)
    }
  }

  openComparisonReportAgent(event) {
    event?.preventDefault()

    if (!this.hasComparisonReportAgentTarget) return

    this.comparisonReportAgentTarget.dataset.open = "true"
    this.openEmbeddedKlavyAgent()
    this.persistComparisonReportPanelState()
    this.syncComparisonReportAgentControls()
  }

  closeComparisonReportAgent(event) {
    event?.preventDefault()

    if (!this.hasComparisonReportAgentTarget) return

    this.comparisonReportAgentTarget.dataset.open = "false"
    this.closeEmbeddedKlavyAgent()
    this.persistComparisonReportPanelState()
    this.syncComparisonReportAgentControls()
  }

  syncComparisonReportAgentControls() {
    if (!this.hasComparisonReportAgentTarget) return

    const isOpen = this.comparisonReportAgentTarget.dataset.open === "true"
    if (this.hasComparisonReportAgentToggleTarget) {
      this.comparisonReportAgentToggleTargets.forEach((button) => {
        const label = isOpen ? (button.dataset.closeLabel || "Agent 닫기") : (button.dataset.openLabel || "Agent 열기")
        button.setAttribute("aria-expanded", isOpen ? "true" : "false")
        button.setAttribute("aria-label", label)
        button.setAttribute("title", label)
        if (!button.hasAttribute("data-icon-button")) button.textContent = label
      })
    }
    if (this.hasComparisonReportAgentLauncherTarget) {
      this.comparisonReportAgentLauncherTargets.forEach((button) => {
        button.hidden = isOpen
        button.setAttribute("aria-expanded", isOpen ? "true" : "false")
      })
    }
  }

  attachComparisonReportAgentCloseListener() {
    if (!this.hasComparisonReportAgentTarget) return

    this.comparisonReportAgentTarget.addEventListener("click", this.handleComparisonReportAgentClick)
  }

  detachComparisonReportAgentCloseListener() {
    if (!this.hasComparisonReportAgentTarget) return

    this.comparisonReportAgentTarget.removeEventListener("click", this.handleComparisonReportAgentClick)
  }

  handleComparisonReportAgentClick(event) {
    const toggle = event.target.closest("[data-action*='klavy-agent#toggle']")
    if (!toggle || toggle.id === "ka-toggle-btn") return

    window.setTimeout(() => this.closeComparisonReportAgent(), 0)
  }

  embeddedKlavyAgentController() {
    if (!this.hasComparisonReportAgentTarget) return null

    const element = this.comparisonReportAgentTarget.querySelector("[data-controller~='klavy-agent']")
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, "klavy-agent")
  }

  openEmbeddedKlavyAgent() {
    this.embeddedKlavyAgentController()?.open?.()
  }

  closeEmbeddedKlavyAgent() {
    this.embeddedKlavyAgentController()?.close?.()
  }

  switchWorkspaceTab(event) {
    event.preventDefault()
    const tab = event.currentTarget?.dataset?.workspaceTab || "service"
    this.activeTabValue = tab === "profile" ? "profile" : "service"
    this.syncWorkspaceTabUrl()
    this.applyWorkspaceTab()
  }

  togglePackageMode() {
    this.applyPackageModeState()
  }

  enableExtraOption(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-extra-option-row]")
    if (!row) return

    this.setExtraOptionEnabled(row, true)
  }

  disableExtraOption(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-extra-option-row]")
    if (!row) return

    this.setExtraOptionEnabled(row, false)
  }

  handleKeywordKeydown(event) {
    if (!["Enter", ",", "Tab"].includes(event.key)) return
    if (event.key === "Tab" && event.currentTarget.value.trim() === "") return

    event.preventDefault()
    this.addKeywordFromInput()
  }

  updateKeywordInputCount() {
    if (!this.hasKeywordInputTarget || !this.hasKeywordCharCountTarget) return

    this.keywordCharCountTarget.textContent = `${this.keywordInputTarget.value.length}/20`
  }

  addKeywordFromInput() {
    if (!this.hasKeywordInputTarget) return

    const values = this.keywordInputTarget.value
      .split(/[,\n]/)
      .map((value) => value.trim())
      .filter(Boolean)
    if (values.length === 0) {
      this.updateKeywordEditorState()
      return
    }

    const keywords = this.keywordValues()
    values.forEach((value) => {
      if (keywords.length < 5 && !keywords.includes(value)) keywords.push(value)
    })

    this.keywordInputTarget.value = ""
    if (keywords.length > this.keywordValues().length) {
      this.renderKeywords(keywords)
    } else {
      this.updateKeywordEditorState()
    }
  }

  removeKeyword(event) {
    event.preventDefault()

    const chip = event.currentTarget.closest("[data-keyword-chip]")
    if (!chip) return

    const value = chip.dataset.keywordValue
    this.renderKeywords(this.keywordValues().filter((keyword) => keyword !== value))
  }

  addFaq(event) {
    event.preventDefault()

    if (!this.hasFaqListTarget || !this.hasFaqTemplateTarget) return
    if (this.visibleFaqRows().length >= 50) return

    const index = this.nextNestedIndex("faq")
    const content = this.faqTemplateTarget.innerHTML.split("__INDEX__").join(index)
    const template = document.createElement("template")
    template.innerHTML = content.trim()
    const row = template.content.firstElementChild
    if (!row) return

    this.faqListTarget.appendChild(row)
    row.querySelectorAll("[data-kmong-service-registration-draft-target~='countedField']").forEach((field) => {
      this.updateCounterFor(field)
    })
    this.updateFaqPositions()
    this.updateFaqCount()

    const firstField = row.querySelector("input:not([type='hidden']), textarea")
    if (firstField) firstField.focus()
  }

  removeFaq(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-faq-row]")
    if (!row) return

    const destroyInput = row.querySelector("input[name$='[_destroy]']")
    if (destroyInput) {
      destroyInput.value = "1"
      row.hidden = true
      row.querySelectorAll("input, textarea, select, button").forEach((control) => {
        if (control !== destroyInput && control.type !== "hidden") control.disabled = true
      })
    } else {
      row.remove()
    }

    this.updateFaqPositions()
    this.updateFaqCount()
  }

  addWorkRequest(event) {
    event.preventDefault()

    if (!this.hasWorkRequestListTarget || !this.hasWorkRequestTemplateTarget) return

    const index = this.nextNestedIndex("workRequest")
    const content = this.workRequestTemplateTarget.innerHTML.split("__INDEX__").join(index)
    const template = document.createElement("template")
    template.innerHTML = content.trim()
    const row = template.content.firstElementChild
    if (!row) return

    this.workRequestListTarget.appendChild(row)
    row.querySelectorAll("[data-kmong-service-registration-draft-target~='countedField']").forEach((field) => {
      this.updateCounterFor(field)
    })
    this.updateWorkRequestPositions()

    const firstField = row.querySelector("input:not([type='hidden']), textarea")
    if (firstField) firstField.focus()
  }

  removeWorkRequest(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-work-request-row]")
    if (!row) return

    row.remove()
    this.updateWorkRequestPositions()
  }

  addWorkRequestOption(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-work-request-row]")
    const list = row?.querySelector("[data-work-request-option-list]")
    const select = row?.querySelector("[data-feature-select]")
    if (!row || !list || !select) return

    const fieldName = (select.dataset.featureFieldName || "").replace("[answer_type]", "[options]")
    if (!fieldName) return

    const index = this.nextNestedIndex("workRequestOption")
    const fieldKey = `pre_work_request_${index}_option`
    const option = document.createElement("div")
    option.className = "ksrd-faq-field ksrd-prework-option"
    option.dataset.workRequestOption = "true"
    option.innerHTML = `
      <input type="text"
        name="${fieldName}[${index}]"
        class="ksrd-faq-input"
        maxlength="255"
        placeholder="의뢰인이 선택할 답변을 입력해주세요"
        data-action="input->kmong-service-registration-draft#updateCount"
        data-kmong-service-registration-draft-target="countedField"
        data-field-key="${fieldKey}"
        data-max-length="255">
      <button type="button"
        class="ksrd-prework-option__delete"
        aria-label="답변 삭제"
        data-action="kmong-service-registration-draft#removeWorkRequestOption">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 6h18"></path>
          <path d="M8 6V4h8v2"></path>
          <path d="M19 6l-1 14H6L5 6"></path>
          <path d="M10 11v5"></path>
          <path d="M14 11v5"></path>
        </svg>
      </button>
      <span class="ksrd-faq-counter" data-kmong-service-registration-draft-target="counter" data-counter-for="${fieldKey}">0/255</span>
    `

    list.appendChild(option)
    const input = option.querySelector("input")
    if (input) {
      this.updateCounterFor(input)
      input.focus()
    }
  }

  removeWorkRequestOption(event) {
    event.preventDefault()

    const option = event.currentTarget.closest("[data-work-request-option]")
    if (option) option.remove()
  }

  copy(event) {
    const fieldKey = event.params.field
    const source = this.copySourceTargets.find((target) => target.dataset.fieldKey === fieldKey)
    if (!source) return

    const text = this.fieldText(source).trim()
    if (!text) {
      this.flashButton(event.currentTarget, "내용 없음", false)
      return
    }

    this.writeClipboard(text)
      .then(() => this.flashButton(event.currentTarget, "복사됨", true))
      .catch(() => this.flashButton(event.currentTarget, "복사 실패", false))
  }

  previewImage(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    const slot = input.closest("[data-image-slot]")
    if (!file || !slot) return

    const preview = slot.querySelector("[data-kmong-service-registration-draft-target~='imagePreview']")
    const label = slot.querySelector("[data-kmong-service-registration-draft-target~='imageLabel']")
    if (!preview) return

    const reader = new FileReader()
    reader.onload = () => {
      preview.innerHTML = ""
      const image = document.createElement("img")
      image.src = reader.result
      image.alt = file.name
      preview.appendChild(image)
      if (label) label.textContent = file.name
    }
    reader.readAsDataURL(file)
  }

  toggleFeatureSelect(event) {
    event.preventDefault()

    const select = event.currentTarget.closest("[data-feature-select]")
    if (!select) return

    if (select.classList.contains("is-open")) {
      this.closeFeatureSelect(select)
    } else {
      this.closeFeatureSelects(select)
      this.openFeatureSelect(select)
    }
  }

  chooseFeatureOption(event) {
    event.preventDefault()

    const option = event.currentTarget
    const select = option.closest("[data-feature-select]")
    if (!select) return

    const value = option.dataset.featureOptionValue
    const mode = select.dataset.featureMode || "single"
    let values = this.featureSelectedValues(select)

    if (mode === "multiple") {
      values = values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
    } else {
      values = [value]
    }

    this.updateFeatureSelect(select, values)

    if (mode !== "multiple") {
      this.closeFeatureSelect(select)
    }
  }

  filterFeatureOptions(event) {
    const select = event.currentTarget.closest("[data-feature-select]")
    if (!select) return

    this.filterFeatureOptionsFor(select, event.currentTarget.value)
  }

  handleFeatureSearchKeydown(event) {
    if (event.key !== "Escape") return

    event.preventDefault()
    const select = event.currentTarget.closest("[data-feature-select]")
    if (select) this.closeFeatureSelect(select)
  }

  toggleMediaAssetPicker(event) {
    event.preventDefault()

    const card = event.currentTarget.closest("[data-media-card], [data-avatar-card]")
    const picker = card?.querySelector("[data-media-asset-picker]")
    if (!picker) return

    picker.hidden = !picker.hidden
  }

  async selectMediaAsset(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.attachUrl
    const mediaAssetId = button.dataset.mediaAssetId
    const role = button.dataset.mediaRole || "detail"
    if (!url || !mediaAssetId) return

    button.disabled = true
    button.dataset.loading = "true"

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          media_asset_id: mediaAssetId,
          role
        })
      })

      if (!response.ok) throw new Error("asset attach failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      delete button.dataset.loading
      button.textContent = "첨부 실패"
      window.setTimeout(() => {
        button.textContent = "선택"
      }, 1400)
    }
  }

  async uploadMediaAsset(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    const url = input.dataset.uploadUrl
    const role = input.dataset.mediaRole || "detail"
    if (!file || !url) return

    const picker = input.closest("[data-media-asset-picker]")
    const uploadButton = picker?.querySelector("[data-media-upload-button]")
    if (uploadButton) {
      uploadButton.setAttribute("aria-disabled", "true")
      uploadButton.dataset.loading = "true"
      uploadButton.dataset.originalText = uploadButton.textContent
      uploadButton.textContent = "등록 중"
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("role", role)

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: formData
      })

      if (!response.ok) throw new Error("asset upload failed")
      window.location.reload()
    } catch (_error) {
      input.value = ""
      if (uploadButton) {
        uploadButton.textContent = "등록 실패"
        window.setTimeout(() => {
          uploadButton.removeAttribute("aria-disabled")
          delete uploadButton.dataset.loading
          uploadButton.textContent = uploadButton.dataset.originalText || "새 이미지 등록"
        }, 1400)
      }
    }
  }

  async selectAvatarAsset(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.avatarAttachUrl
    const mediaAssetId = button.dataset.mediaAssetId
    if (!url || !mediaAssetId) return

    button.disabled = true
    button.dataset.loading = "true"

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ media_asset_id: mediaAssetId })
      })

      if (!response.ok) throw new Error("avatar attach failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      delete button.dataset.loading
      button.textContent = "첨부 실패"
      window.setTimeout(() => {
        button.textContent = "선택"
      }, 1400)
    }
  }

  async uploadAvatarAsset(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    const url = input.dataset.uploadUrl
    if (!file || !url) return

    const card = input.closest("[data-avatar-card]")
    this.previewAvatarFile(card, file)

    const picker = input.closest("[data-media-asset-picker]")
    const uploadButton = picker?.querySelector("[data-media-upload-button]")
    if (uploadButton) {
      uploadButton.setAttribute("aria-disabled", "true")
      uploadButton.dataset.loading = "true"
      uploadButton.dataset.originalText = uploadButton.textContent
      uploadButton.textContent = "등록 중"
    }

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: formData
      })

      if (!response.ok) throw new Error("avatar upload failed")
      window.location.reload()
    } catch (_error) {
      input.value = ""
      if (uploadButton) {
        uploadButton.textContent = "등록 실패"
        window.setTimeout(() => {
          uploadButton.removeAttribute("aria-disabled")
          delete uploadButton.dataset.loading
          uploadButton.textContent = uploadButton.dataset.originalText || "새 이미지 등록"
        }, 1400)
      }
    }
  }

  async deleteAvatar(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.avatarDeleteUrl
    if (!url) return

    button.disabled = true
    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      })

      if (!response.ok) throw new Error("avatar delete failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      button.textContent = "삭제 실패"
      window.setTimeout(() => {
        button.textContent = "삭제"
      }, 1400)
    }
  }

  async detachVideo(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.detachUrl
    if (!url) return

    button.disabled = true
    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      })

      if (!response.ok) throw new Error("video detach failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      button.textContent = "삭제 실패"
      window.setTimeout(() => {
        button.textContent = "삭제"
      }, 1400)
    }
  }

  async detachImage(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.detachUrl
    if (!url) return

    button.disabled = true
    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      })

      if (!response.ok) throw new Error("image detach failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      button.textContent = "삭제 실패"
      window.setTimeout(() => {
        button.textContent = "삭제"
      }, 1400)
    }
  }

  async uploadVideoThumbnail(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    const url = input.dataset.thumbnailUrl
    if (!file || !url) return

    const label = input.closest("[data-video-row]")?.querySelector("[data-thumbnail-upload-label]")
    if (label) {
      label.dataset.originalText = label.textContent
      label.textContent = "업로드 중"
    }

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: formData
      })

      if (!response.ok) throw new Error("thumbnail upload failed")
      window.location.reload()
    } catch (_error) {
      input.value = ""
      if (label) {
        label.textContent = "업로드 실패"
        window.setTimeout(() => {
          label.textContent = label.dataset.originalText || "썸네일 업로드"
        }, 1400)
      }
    }
  }

  toggleVideoThumbnailPicker(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-video-row]")
    const picker = row?.querySelector("[data-video-thumbnail-picker]")
    if (!picker) return

    picker.hidden = !picker.hidden
  }

  async selectVideoThumbnailAsset(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.thumbnailUrl
    const mediaAssetId = button.dataset.mediaAssetId
    if (!url || !mediaAssetId) return

    button.disabled = true
    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ media_asset_id: mediaAssetId })
      })

      if (!response.ok) throw new Error("thumbnail attach failed")
      window.location.reload()
    } catch (_error) {
      button.disabled = false
      button.textContent = "첨부 실패"
      window.setTimeout(() => {
        button.textContent = "선택"
      }, 1400)
    }
  }

  startVideoDrag(event) {
    const row = event.currentTarget.closest("[data-video-row]")
    if (!row) return

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.videoId)
    row.classList.add("is-dragging")
  }

  dragOverVideo(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-video-row]")
    if (!row) return

    event.dataTransfer.dropEffect = "move"
    row.classList.add("is-drag-over")
  }

  dropVideo(event) {
    event.preventDefault()

    const targetRow = event.currentTarget.closest("[data-video-row]")
    const list = targetRow?.closest("[data-video-list]")
    const draggedId = event.dataTransfer.getData("text/plain")
    const draggedRow = list?.querySelector(`[data-video-id="${draggedId}"]`)
    if (!list || !targetRow || !draggedRow || draggedRow === targetRow) return

    const targetRect = targetRow.getBoundingClientRect()
    const insertAfter = event.clientY > targetRect.top + targetRect.height / 2
    list.insertBefore(draggedRow, insertAfter ? targetRow.nextElementSibling : targetRow)
    this.clearVideoDragState(list)
    this.persistVideoOrder(list)
  }

  endVideoDrag(event) {
    const list = event.currentTarget.closest("[data-video-list]")
    if (list) this.clearVideoDragState(list)
  }

  clearVideoDragState(list) {
    list.querySelectorAll("[data-video-row]").forEach((row) => {
      row.classList.remove("is-dragging", "is-drag-over")
    })
  }

  async persistVideoOrder(list) {
    const url = list.dataset.videoReorderUrl
    const videoIds = Array.from(list.querySelectorAll("[data-video-row]")).map((row) => row.dataset.videoId)
    if (!url || videoIds.length === 0) return

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ video_ids: videoIds })
      })

      if (!response.ok) throw new Error("video reorder failed")
    } catch (_error) {
      window.location.reload()
    }
  }

  startImageDrag(event) {
    const row = event.currentTarget.closest("[data-image-row]")
    if (!row) return

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.imageId)
    row.classList.add("is-dragging")
  }

  dragOverImage(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-image-row]")
    if (!row) return

    event.dataTransfer.dropEffect = "move"
    row.classList.add("is-drag-over")
  }

  dropImage(event) {
    event.preventDefault()

    const targetRow = event.currentTarget.closest("[data-image-row]")
    const list = targetRow?.closest("[data-image-list]")
    const draggedId = event.dataTransfer.getData("text/plain")
    const draggedRow = list?.querySelector(`[data-image-id="${draggedId}"]`)
    if (!list || !targetRow || !draggedRow || draggedRow === targetRow) return

    const targetRect = targetRow.getBoundingClientRect()
    const insertAfter = event.clientY > targetRect.top + targetRect.height / 2
    list.insertBefore(draggedRow, insertAfter ? targetRow.nextElementSibling : targetRow)
    this.clearImageDragState(list)
    this.persistImageOrder(list)
  }

  endImageDrag(event) {
    const list = event.currentTarget.closest("[data-image-list]")
    if (list) this.clearImageDragState(list)
  }

  clearImageDragState(list) {
    list.querySelectorAll("[data-image-row]").forEach((row) => {
      row.classList.remove("is-dragging", "is-drag-over")
    })
  }

  async persistImageOrder(list) {
    const url = list.dataset.imageReorderUrl
    const imageIds = Array.from(list.querySelectorAll("[data-image-row]")).map((row) => row.dataset.imageId)
    if (!url || imageIds.length === 0) return

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ image_ids: imageIds })
      })

      if (!response.ok) throw new Error("image reorder failed")
    } catch (_error) {
      window.location.reload()
    }
  }

  openFeatureSelect(select) {
    const menu = select.querySelector("[data-feature-menu]")
    const trigger = select.querySelector(".ksrd-feature-select__trigger")
    if (!menu || !trigger) return

    select.classList.add("is-open")
    menu.hidden = false
    trigger.setAttribute("aria-expanded", "true")

    const search = menu.querySelector("input[type='search']")
    if (search) {
      search.value = ""
      this.filterFeatureOptionsFor(select, "")
      window.requestAnimationFrame(() => search.focus())
    }
  }

  closeFeatureSelect(select) {
    const menu = select.querySelector("[data-feature-menu]")
    const trigger = select.querySelector(".ksrd-feature-select__trigger")

    select.classList.remove("is-open")
    if (menu) menu.hidden = true
    if (trigger) trigger.setAttribute("aria-expanded", "false")
  }

  closeFeatureSelects(except = null) {
    this.featureSelectTargets.forEach((select) => {
      if (select !== except) this.closeFeatureSelect(select)
    })
  }

  closeFeatureSelectOnOutsideClick(event) {
    if (event.target.closest("[data-feature-select]")) return

    this.closeFeatureSelects()
  }

  closeFeatureSelectOnEscape(event) {
    if (event.key === "Escape") this.closeFeatureSelects()
  }

  filterFeatureOptionsFor(select, query) {
    const normalizedQuery = query.toString().trim().toLocaleLowerCase("ko-KR")

    select.querySelectorAll("[data-feature-option]").forEach((option) => {
      const text = option.dataset.featureOptionValue.toString().toLocaleLowerCase("ko-KR")
      option.classList.toggle("is-filtered", normalizedQuery.length > 0 && !text.includes(normalizedQuery))
    })
  }

  featureSelectedValues(select) {
    try {
      const values = JSON.parse(select.dataset.selectedValues || "[]")
      return Array.isArray(values) ? values.map((value) => value.toString()).filter(Boolean) : []
    } catch (_error) {
      return []
    }
  }

  featureSelectedLabels(select, values) {
    if (values.length === 0) return select.dataset.featurePlaceholder || ""

    return values.map((value) => {
      const option = Array.from(select.querySelectorAll("[data-feature-option]")).find((item) => {
        return item.dataset.featureOptionValue === value
      })
      return option?.dataset.featureOptionLabel || value
    }).join(", ")
  }

  updateFeatureSelect(select, values) {
    const validValues = Array.from(select.querySelectorAll("[data-feature-option]")).map((option) => option.dataset.featureOptionValue)
    const nextValues = [...new Set(values.map((value) => value.toString()).filter((value) => validValues.includes(value)))]
    const valueLabel = select.querySelector("[data-feature-value]")
    const hiddenList = select.querySelector("[data-feature-hidden-list]")
    const fieldName = select.dataset.featureFieldName
    const mode = select.dataset.featureMode || "single"

    select.dataset.selectedValues = JSON.stringify(nextValues)

    if (valueLabel) valueLabel.textContent = this.featureSelectedLabels(select, nextValues)

    select.querySelectorAll("[data-feature-option]").forEach((option) => {
      option.setAttribute("aria-selected", nextValues.includes(option.dataset.featureOptionValue) ? "true" : "false")
    })

    if (!hiddenList || !fieldName) return

    hiddenList.innerHTML = ""
    if (mode === "multiple") {
      hiddenList.appendChild(this.buildHiddenInput(`${fieldName}[]`, ""))
      nextValues.forEach((value) => hiddenList.appendChild(this.buildHiddenInput(`${fieldName}[]`, value)))
    } else {
      hiddenList.appendChild(this.buildHiddenInput(fieldName, nextValues[0] || ""))
    }

    this.updateWorkRequestOptionVisibility(select)
  }

  keywordValues() {
    if (!this.hasKeywordHiddenListTarget) return []

    return Array.from(this.keywordHiddenListTarget.querySelectorAll("input[data-keyword-hidden]"))
      .map((input) => input.value.trim())
      .filter(Boolean)
  }

  renderKeywords(keywords) {
    const nextKeywords = [...new Set(keywords.map((keyword) => keyword.trim()).filter(Boolean))].slice(0, 5)

    if (this.hasKeywordHiddenListTarget) {
      this.keywordHiddenListTarget.innerHTML = ""
      this.keywordHiddenListTarget.appendChild(this.buildHiddenInput("service_registration_draft[search_keywords][]", ""))
      nextKeywords.forEach((keyword) => {
        const input = this.buildHiddenInput("service_registration_draft[search_keywords][]", keyword)
        input.dataset.keywordHidden = "true"
        this.keywordHiddenListTarget.appendChild(input)
      })
    }

    if (this.hasKeywordChipListTarget) {
      this.keywordChipListTarget.innerHTML = ""
      nextKeywords.forEach((keyword) => {
        const chip = document.createElement("span")
        chip.className = "ksrd-keyword-chip"
        chip.dataset.keywordChip = "true"
        chip.dataset.keywordValue = keyword

        const label = document.createElement("span")
        label.textContent = keyword
        chip.appendChild(label)

        const button = document.createElement("button")
        button.type = "button"
        button.setAttribute("aria-label", `${keyword} 삭제`)
        button.dataset.action = "kmong-service-registration-draft#removeKeyword"
        button.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12"></path>
          </svg>
        `
        chip.appendChild(button)

        this.keywordChipListTarget.appendChild(chip)
      })
    }

    this.updateKeywordEditorState()
  }

  updateKeywordEditorState() {
    const count = this.keywordValues().length

    if (this.hasKeywordCountTarget) this.keywordCountTarget.textContent = count.toString()
    if (this.hasKeywordInputTarget) {
      this.keywordInputTarget.disabled = this.keywordInputTarget.dataset.readonly === "true" || count >= 5
      if (count >= 5) this.keywordInputTarget.value = ""
    }
    this.updateKeywordInputCount()
  }

  applyPackageModeState() {
    if (!this.hasPackageModeToggleTarget || !this.hasPackageCardTarget) return
    if (this.packageModeToggleTarget.disabled) return

    const packageModeEnabled = this.packageModeToggleTarget.checked
    this.packageCardTargets.forEach((card) => {
      const disabled = !packageModeEnabled && card.dataset.packageTier !== "standard"
      card.classList.toggle("is-disabled", disabled)

      card.querySelectorAll("input, textarea, select").forEach((control) => {
        if (control.type === "hidden") return
        control.disabled = disabled || control.dataset.readonly === "true"
      })
    })
  }

  setExtraOptionEnabled(row, enabled) {
    const addButton = row.querySelector("[data-extra-add]")
    const card = row.querySelector("[data-extra-card]")
    const enabledInput = row.querySelector("[data-extra-enabled-input]")

    if (enabledInput) enabledInput.checked = enabled
    if (addButton) addButton.hidden = enabled
    if (card) card.hidden = !enabled
  }

  visibleFaqRows() {
    if (!this.hasFaqListTarget) return []

    return Array.from(this.faqListTarget.querySelectorAll("[data-faq-row]")).filter((row) => !row.hidden)
  }

  updateFaqPositions() {
    this.visibleFaqRows().forEach((row, index) => {
      const positionInput = row.querySelector("input[name$='[position]']")
      if (positionInput) positionInput.value = index + 1
    })
  }

  updateFaqCount() {
    if (!this.hasFaqCountTarget) return

    this.faqCountTarget.textContent = this.visibleFaqRows().length.toString()
  }

  updateWorkRequestPositions() {
    if (!this.hasWorkRequestListTarget) return

    Array.from(this.workRequestListTarget.querySelectorAll("[data-work-request-row]")).forEach((row, index) => {
      const positionInput = row.querySelector("input[name$='[position]']")
      if (positionInput) positionInput.value = index + 1
    })
  }

  updateWorkRequestOptionVisibility(targetSelect = null) {
    const selects = targetSelect ? [targetSelect] : Array.from(this.element.querySelectorAll("[data-work-request-row] [data-feature-select]"))

    selects.forEach((select) => {
      const row = select.closest("[data-work-request-row]")
      const options = row?.querySelector("[data-work-request-options]")
      if (!options) return

      const selected = this.featureSelectedValues(select)[0] || ""
      options.hidden = !["single_choice", "multiple_choice"].includes(selected)
    })
  }

  nextNestedIndex(prefix) {
    const key = `${prefix}Index`
    this[key] = (this[key] || Date.now()) + 1
    return `new_${this[key]}`
  }

  buildHiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  reloadPage() {
    window.location.reload()
  }

  previewAvatarFile(card, file) {
    if (!card || !file) return

    const preview = card.querySelector("[data-avatar-preview]")
    const filename = card.querySelector("[data-avatar-filename]")
    if (!preview) return

    const src = URL.createObjectURL(file)
    let image = preview.querySelector("[data-avatar-preview-image]")
    const fallback = preview.querySelector("[data-avatar-preview-fallback]")
    if (fallback) fallback.remove()

    if (!image) {
      image = document.createElement("img")
      image.dataset.avatarPreviewImage = ""
      image.alt = "프로필 이미지 미리보기"
      preview.prepend(image)
    }
    image.src = src
    image.onload = () => URL.revokeObjectURL(src)
    if (filename) filename.textContent = file.name
  }

  syncWorkspaceTabUrl() {
    if (!window.history?.replaceState) return

    const url = new URL(window.location.href)
    if (this.activeTabValue === "profile") {
      url.searchParams.set("tab", "profile")
      url.searchParams.delete("comparison")
    } else {
      url.searchParams.delete("tab")
      url.searchParams.delete("comparison")
    }
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
  }

  updateCounterFor(field) {
    const fieldKey = field.dataset.fieldKey
    const counter = this.counterTargets.find((target) => target.dataset.counterFor === fieldKey)
    if (!counter) return

    const max = Number.parseInt(field.dataset.maxLength || "0", 10)
    const count = this.fieldText(field).length
    counter.textContent = max > 0 ? `${count}/${max}` : `${count}`
    counter.classList.toggle("is-warn", max > 0 && count >= Math.floor(max * 0.9) && count <= max)
    counter.classList.toggle("is-over", max > 0 && count > max)
  }

  writeClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text)
    }

    return new Promise((resolve, reject) => {
      const textarea = document.createElement("textarea")
      textarea.value = text
      textarea.setAttribute("readonly", "")
      textarea.style.position = "fixed"
      textarea.style.top = "-9999px"
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand("copy") ? resolve() : reject(new Error("copy failed"))
      } catch (error) {
        reject(error)
      } finally {
        document.body.removeChild(textarea)
      }
    })
  }

  flashButton(button, message, success) {
    const label = button.querySelector("[data-copy-label]")
    const originalLabel = label ? label.textContent : button.textContent

    if (label) label.textContent = message
    button.dataset.copyState = success ? "success" : "error"
    button.style.borderColor = success ? "#A7F3D0" : "#FCA5A5"
    button.style.background = success ? "#ECFDF5" : "#FEF2F2"
    button.style.color = success ? "#047857" : "#B91C1C"

    window.setTimeout(() => {
      if (label) label.textContent = originalLabel
      button.style.borderColor = ""
      button.style.background = ""
      button.style.color = ""
      delete button.dataset.copyState
    }, 1300)
  }

  openComparisonModal(event) {
    event?.preventDefault()
    const modal = this.element.querySelector("[data-comparison-modal]")
    if (modal) modal.hidden = false
    this.syncComparisonRowOrderState()
  }

  closeComparisonModal(event) {
    event?.preventDefault()
    if (this.comparisonSaveInFlight) this.cancelComparisonSave()
    const modal = this.element.querySelector("[data-comparison-modal]")
    if (modal) modal.hidden = true
  }

  syncComparisonRow(event) {
    const row = event.currentTarget.closest("[data-comparison-row]")
    this.replaceComparisonValueControl(row)
  }

  toggleComparisonSelect(event) {
    event.preventDefault()
    const select = event.currentTarget.closest(".ksrd-comparison-select")
    if (!select) return

    if (select.classList.contains("is-open")) {
      this.closeComparisonSelect(select)
    } else {
      this.closeComparisonSelects(select)
      this.openComparisonSelect(select)
    }
  }

  chooseComparisonOption(event) {
    event.preventDefault()
    const option = event.currentTarget
    const select = option.closest(".ksrd-comparison-select")
    if (!select) return

    select.dataset.value = option.dataset.optionValue || ""
    const label = select.querySelector("[data-comparison-select-label]")
    if (label) label.textContent = option.dataset.optionLabel || option.textContent.trim()
    select.querySelectorAll(".ksrd-comparison-select__option").forEach((item) => {
      item.setAttribute("aria-selected", item === option ? "true" : "false")
    })
    this.closeComparisonSelect(select)
    if (select.hasAttribute("data-comparison-input-type")) {
      this.replaceComparisonValueControl(select.closest("[data-comparison-row]"))
    }
  }

  addComparisonRow(event) {
    event?.preventDefault()
    const list = this.element.querySelector("[data-comparison-row-list]")
    if (!list) return

    const row = document.createElement("div")
    row.className = "ksrd-comparison-row"
    row.dataset.comparisonRow = "true"
    row.innerHTML = `
      ${this.comparisonRowOrderHtml()}
      ${this.comparisonSelectHtml("data-comparison-source-role", "competitor", [["competitor", "경쟁 상품"], ["own", "내 상품"]])}
      ${this.comparisonSelectHtml("data-comparison-input-type", "url", [["url", "URL"], ["text", "텍스트"]])}
      <input type="url" data-comparison-value placeholder="https://kmong.com/..." autocomplete="off">
      <button type="button" class="ksrd-source__button ksrd-source__button--danger ksrd-comparison-row__delete" data-icon-button data-action="kmong-service-registration-draft#removeComparisonRow" aria-label="비교 대상 삭제" title="삭제">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
      </button>
      <div class="ksrd-comparison-row__status" data-comparison-row-status>대기 중</div>
      ${this.comparisonRowProgressHtml()}
    `
    list.appendChild(row)
    this.syncComparisonRowOrderState()
  }

  async removeComparisonRow(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const row = button.closest("[data-comparison-row]")
    if (!row) return

    const deleteUrl = row.dataset.comparisonSourceDeleteUrl
    if (deleteUrl) {
      if (!window.confirm("이 비교 대상을 삭제할까요?")) return

      await this.withBusyButton(button, "삭제 중", async () => {
        const response = await this.jsonFetch(deleteUrl, { method: "DELETE" })
        if (!response.ok) throw new Error(response.error || "비교 대상을 삭제하지 못했습니다.")
        const deletedSourceId = row.dataset.comparisonSourceId || response.data?.id?.toString() || ""
        this.removeComparisonRowElement(row)
        this.removeComparisonSourceTab(deletedSourceId)
        this.clearDeletedSourceFromUrl(deletedSourceId)
        this.setComparisonProgress("비교 대상을 삭제했습니다. 이어서 다른 항목을 삭제하거나 추가할 수 있습니다.", "done")
      })
      return
    }

    const rows = Array.from(this.element.querySelectorAll("[data-comparison-row]"))
    if (rows.length <= 1) {
      row.querySelector("[data-comparison-value]")?.focus()
      return
    }
    this.removeComparisonRowElement(row)
  }

  removeComparisonRowElement(row) {
    if (!row) return

    this.stopComparisonRowPolling(row)
    row.remove()

    const list = this.element.querySelector("[data-comparison-row-list]")
    if (list && list.querySelectorAll("[data-comparison-row]").length === 0) {
      this.addComparisonRow()
    }
    this.syncComparisonRowOrderState()
  }

  removeComparisonSourceTab(sourceId) {
    if (!sourceId) return

    const tab = this.element.querySelector(`[data-source-tab="${sourceId}"]`)
    const wasActive = tab?.classList.contains("is-active")
    tab?.remove()
    this.renumberComparisonSourceTabs()

    if (wasActive) {
      const nextTab = this.element.querySelector("[data-source-tab]")
      nextTab?.classList.add("is-active")
    }
  }

  renumberComparisonSourceTabs() {
    let competitorIndex = 0
    this.element.querySelectorAll("[data-source-tab]").forEach((tab) => {
      const label = tab.querySelector("span")
      if (!label) return

      if (tab.dataset.sourceRole === "own") {
        label.textContent = "내 상품"
      } else if (tab.dataset.sourceRole === "improved") {
        return
      } else {
        competitorIndex += 1
        label.textContent = `경쟁상품 ${competitorIndex}`
      }
    })
  }

  reorderComparisonSourceTabsFromRows() {
    const tabList = this.element.querySelector(".ksrd-source-tabs")
    if (!tabList) return

    this.element.querySelectorAll("[data-comparison-row]").forEach((row) => {
      const sourceId = row.dataset.comparisonSourceId
      if (!sourceId) return

      const tab = tabList.querySelector(`[data-source-tab="${sourceId}"]`)
      if (tab) tabList.appendChild(tab)
    })
    this.renumberComparisonSourceTabs()
  }

  clearDeletedSourceFromUrl(sourceId) {
    if (!sourceId || !window.history?.replaceState) return

    const url = new URL(window.location.href)
    if (url.searchParams.get("source") !== sourceId) return

    url.searchParams.delete("source")
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
  }

  async saveComparisonSources(event) {
    event?.preventDefault()
    const button = event.currentTarget
    if (this.comparisonSaveInFlight) return

    if (button?.dataset?.comparisonSaveComplete === "true") {
      this.reloadComparisonContext()
      return
    }

    const url = button.dataset.comparisonSourceUrl
    if (!url) return
    delete button.dataset.comparisonSaveComplete

    const entries = Array.from(this.element.querySelectorAll("[data-comparison-row]"))
      .map((row, index) => {
        const inputType = row.querySelector("[data-comparison-input-type]")?.dataset.value || "url"
        const value = row.querySelector("[data-comparison-value]")?.value?.trim() || ""
        const sourceId = row.dataset.comparisonSourceId || ""
        return {
          row,
          sourceId,
          updateUrl: row.dataset.comparisonSourceUpdateUrl || "",
          importUrl: row.dataset.comparisonSourceImportUrl || "",
          payload: {
            source_role: row.querySelector("[data-comparison-source-role]")?.dataset.value || "competitor",
            input_type: inputType,
            position: index + 1,
            source_url: inputType === "url" ? value : "",
            raw_text: inputType === "text" ? value : ""
          }
        }
      })
      .filter((entry) => entry.payload.source_url || entry.payload.raw_text)

    if (entries.length === 0) {
      this.flashButton(button, "입력 필요", false)
      this.setComparisonProgress("등록할 URL 또는 텍스트를 입력해 주세요.", "error")
      return
    }

    const refreshAll = button.dataset.comparisonSaveMode === "all"
    const entriesToProcess = refreshAll ? entries : entries.filter((entry) => !entry.sourceId || this.comparisonEntryChanged(entry))
    if (entriesToProcess.length === 0) {
      this.flashButton(button, "변경 없음", true)
      this.setComparisonProgress("새로 추가되거나 수정된 비교 대상이 없습니다. 기존 항목까지 다시 읽으려면 전체 저장을 눌러 주세요.", "done")
      return
    }

    const modeLabel = refreshAll ? "전체 항목" : "추가/변경 항목"
    let failureCount = 0
    this.comparisonSaveInFlight = true
    this.comparisonSaveCancelled = false
    this.stopFreshnessPolling()
    this.stopComparisonSaveWatch()
    if (button) {
      button.disabled = true
      button.textContent = "등록 중"
    }
    this.setComparisonModalLocked(true)
    this.setComparisonProgress(`0/${entriesToProcess.length}개 ${modeLabel} 등록 작업을 큐에 넣는 중`, "running")
    entriesToProcess.forEach((entry) => {
      this.setComparisonRowStatus(entry.row, entry.sourceId ? `${modeLabel} 처리 대기 중` : "신규 항목 처리 대기 중", "pending")
      this.resetComparisonRowProgressPanel(entry.row, "대기 중입니다.")
    })

    for (const [index, entry] of entriesToProcess.entries()) {
      if (this.comparisonSaveCancelled) break

      const isOwnSource = entry.payload.source_role === "own"
      const stepLabel = entry.payload.input_type === "url"
        ? (isOwnSource ? "URL 읽기, AI 분석, 초안 저장, 이미지 등록 중" : "URL 읽기 및 AI 분석 중")
        : (isOwnSource ? "붙여넣은 텍스트 분석 후 초안 저장 중" : "붙여넣은 텍스트 AI 분석 중")
      this.setComparisonProgress(`${index + 1}/${entriesToProcess.length}개 ${modeLabel} 처리 중`, "running")
      this.setComparisonRowStatus(entry.row, stepLabel, "running")

      try {
        if (!entry.sourceId) {
          const createResponse = await this.jsonFetch(url, {
            method: "POST",
            body: JSON.stringify({ defer_processing: true, competitor_source: entry.payload })
          })
          if (!createResponse.ok) throw new Error(createResponse.error || "비교 대상을 먼저 저장하지 못했습니다.")
          this.attachComparisonRowSource(entry.row, createResponse.data?.source || {})
          entry.sourceId = entry.row.dataset.comparisonSourceId || ""
          entry.updateUrl = entry.row.dataset.comparisonSourceUpdateUrl || ""
          entry.importUrl = entry.row.dataset.comparisonSourceImportUrl || ""
        }
        if (this.comparisonSaveCancelled) {
          await this.cancelComparisonRowProcessing(entry.row)
          break
        }

        const progressRunId = this.startComparisonRowProgress(entry.row, entry.row.dataset.comparisonSourceStatusUrl)
        const response = this.isKmongEditUrl(entry.payload.source_url)
          ? await this.importComparisonEditSource(entry, progressRunId)
          : await this.jsonFetch(entry.updateUrl || url, {
              method: "PATCH",
              body: JSON.stringify({ progress_run_id: progressRunId, competitor_source: entry.payload })
            })
        if (!response.ok) throw new Error(response.error || "비교 대상을 저장하지 못했습니다.")
        const source = response.data?.source || {}
        this.attachComparisonRowSource(entry.row, source)
        if (this.comparisonSaveCancelled) {
          await this.cancelComparisonRowProcessing(entry.row)
          break
        }
        this.renderResponseComparisonRowProgress(entry.row, response)
        this.setComparisonRowStatus(entry.row, "등록 작업을 큐에 넣었습니다.", "running")
      } catch (error) {
        if (this.comparisonSaveCancelled) break
        failureCount += 1
        this.stopComparisonRowPolling(entry.row)
        this.setComparisonRowStatus(entry.row, error.message || "등록 실패", "error")
      }
    }

    if (this.comparisonSaveCancelled) return

    if (failureCount > 0) {
      if (button) {
        button.disabled = false
        button.textContent = "다시 등록"
      }
      this.comparisonSaveInFlight = false
      this.setComparisonModalLocked(false)
      this.startFreshnessPolling()
      this.setComparisonProgress(`${failureCount}개 항목이 실패했습니다. 실패한 항목을 수정한 뒤 다시 등록해 주세요.`, "error")
      return
    }

    const result = await this.waitForComparisonRows(entriesToProcess.map((entry) => entry.row))
    if (result.cancelled || this.comparisonSaveCancelled) return
    if (result.failed > 0 || result.timedOut > 0) {
      if (button) {
        button.disabled = false
        button.textContent = "다시 등록"
      }
      this.comparisonSaveInFlight = false
      this.setComparisonModalLocked(false)
      this.startFreshnessPolling()
      const timeoutText = result.timedOut > 0 ? `, ${result.timedOut}개 항목은 응답 대기 시간이 초과되었습니다` : ""
      this.setComparisonProgress(`${result.failed}개 항목이 실패했습니다${timeoutText}. 실패한 항목을 확인한 뒤 다시 등록해 주세요.`, "error")
      return
    }

    if (button) {
      button.textContent = "완료"
      button.disabled = false
      button.dataset.comparisonSaveComplete = "true"
    }
    this.comparisonSaveInFlight = false
    this.setComparisonModalLocked(false)
    this.stopComparisonRowPollTimers()
    this.hasExternalUpdate = true
    this.stopFreshnessPolling()
    this.setComparisonProgress(`총 ${entriesToProcess.length}개 ${modeLabel} 저장 완료. 화면을 새로고침합니다.`, "done")
    window.setTimeout(() => this.reloadComparisonContext(), 700)
  }

  async importComparisonEditSource(entry, progressRunId) {
    const importUrl = entry.importUrl || entry.row?.dataset?.comparisonSourceImportUrl
    if (!importUrl) throw new Error("크몽 편집 화면 가져오기 주소를 찾지 못했습니다.")

    this.setComparisonRowStatus(entry.row, "크몽 편집 화면을 브라우저에서 읽는 중", "running")
    const extraction = await this.requestKmongEditExtraction({
      sourceUrl: entry.payload.source_url,
      sourceRole: entry.payload.source_role,
      position: entry.payload.position,
      sourceId: entry.sourceId,
      progressRunId
    })
    const importPayload = extraction?.importPayload || extraction?.result?.importPayload
    if (!importPayload) throw new Error("크몽 편집 화면에서 가져온 데이터가 비어 있습니다.")

    return this.jsonFetch(importUrl, {
      method: "POST",
      body: JSON.stringify({
        progress_run_id: progressRunId,
        competitor_source: entry.payload,
        import: importPayload
      })
    })
  }

  requestKmongEditExtraction(detail = {}) {
    return new Promise((resolve, reject) => {
      if (!window.CustomEvent) {
        reject(new Error("브라우저 확장 프로그램 호출을 지원하지 않는 환경입니다."))
        return
      }

      const requestId = `comparison-import-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const timeout = window.setTimeout(() => {
        window.removeEventListener("klavy-kmong:comparison-import-response", handleResponse)
        reject(new Error("Klavy Kmong 확장 프로그램 응답이 없습니다. 확장 프로그램을 새로고침한 뒤 다시 시도해 주세요."))
      }, 30000)

      const handleResponse = (event) => {
        const response = event.detail || {}
        if (response.requestId !== requestId) return

        window.clearTimeout(timeout)
        window.removeEventListener("klavy-kmong:comparison-import-response", handleResponse)
        if (response.ok) {
          resolve(response)
        } else {
          reject(new Error(response.error || "크몽 편집 화면을 읽지 못했습니다."))
        }
      }

      window.addEventListener("klavy-kmong:comparison-import-response", handleResponse)
      window.dispatchEvent(new CustomEvent("klavy-kmong:comparison-import-request", {
        detail: { ...detail, requestId }
      }))
    })
  }

  isKmongEditUrl(value) {
    if (!value) return false

    try {
      const parsed = new URL(value, window.location.origin)
      const host = parsed.hostname.toLowerCase()
      return (host === "kmong.com" || host.endsWith(".kmong.com")) && parsed.pathname.startsWith("/my-gigs/edit/")
    } catch (_error) {
      return false
    }
  }

  switchComparisonSource(event) {
    event?.preventDefault()
    const sourceId = event.currentTarget?.dataset?.sourceTab
    if (!sourceId) return

    this.element.querySelectorAll("[data-source-tab]").forEach((tab) => {
      tab.classList.toggle("is-active", tab.dataset.sourceTab === sourceId)
    })
    this.syncComparisonSourceUrl(sourceId, true)
  }

  async parseComparisonSource(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonParseUrl
    if (!url) return

    await this.withBusyButton(button, "분석 중", async () => {
      const response = await this.jsonFetch(url, { method: "POST" })
      if (!response.ok) throw new Error(response.error || "구조화 분석에 실패했습니다.")
      window.location.href = this.comparisonContextHref()
    })
  }

  async applyComparisonSource(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonSourceApplyUrl
    if (!url) return

    const progressRunId = this.startActiveComparisonProgress(button, "채우는 중", button.dataset.comparisonStatusUrl)
    try {
      this.setComparisonProgress("내 상품 분석 결과를 초안 필드와 이미지 에셋에 저장 중입니다.", "running")
      const response = await this.jsonFetch(url, {
        method: "POST",
        signal: this.activeComparisonRequestController?.signal,
        body: JSON.stringify({ import_images: true, progress_run_id: progressRunId })
      })
      if (!response.ok) throw new Error(response.error || "내 상품 내용을 초안에 적용하지 못했습니다.")
      this.renderResponseComparisonProgress(response)
      this.setComparisonProgress("내 상품 내용 저장이 완료되었습니다. 화면을 새로고침합니다.", "done")
      this.completeActiveComparisonProgress({ restoreButton: true, reload: true })
    } catch (error) {
      if (error?.name === "AbortError") return
      this.failActiveComparisonProgress(error)
    }
  }

  async refreshComparisonSource(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonRefreshUrl
    if (!url) return

    const progressRunId = this.startActiveComparisonProgress(button, "다시 읽는 중", button.dataset.comparisonStatusUrl)
    try {
      const sourceUrl = button.dataset.comparisonSourceUrl || this.element.querySelector("[data-active-comparison-source-url-input]")?.value?.trim() || ""
      const response = this.isKmongEditUrl(sourceUrl) && button.dataset.comparisonSourceImportUrl
        ? await this.importComparisonEditSource({
            row: null,
            sourceId: button.dataset.comparisonSourceId || "",
            importUrl: button.dataset.comparisonSourceImportUrl,
            payload: {
              source_role: button.dataset.comparisonSourceRole || "own",
              input_type: "url",
              source_url: sourceUrl,
              position: button.dataset.comparisonSourcePosition || ""
            }
          }, progressRunId)
        : await this.jsonFetch(url, {
            method: "POST",
            signal: this.activeComparisonRequestController?.signal,
            body: JSON.stringify({ progress_run_id: progressRunId })
          })
      if (!response.ok) throw new Error(response.error || "URL을 다시 읽지 못했습니다.")
      this.renderResponseComparisonProgress(response)
      this.completeActiveComparisonProgress({ restoreButton: true, reload: true })
    } catch (error) {
      if (error?.name === "AbortError") return
      this.failActiveComparisonProgress(error)
    }
  }

  async moveComparisonSource(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonSourceMoveUrl
    const direction = button.dataset.comparisonSourceMoveDirection
    if (!url || !direction) return

    const originalText = button.textContent
    button.disabled = true
    button.textContent = "이동 중"
    try {
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({ direction })
      })
      if (!response.ok) throw new Error(response.error || "비교 대상 순서를 바꾸지 못했습니다.")
      const sourceId = response.data?.source?.id || button.dataset.comparisonSourceId
      const nextUrl = new URL(window.location.href)
      if (sourceId) nextUrl.searchParams.set("source", sourceId)
      window.location.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
    } catch (error) {
      this.flashButton(button, error?.message || "이동 실패", false)
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }

  moveComparisonRowUp(event) {
    event?.preventDefault()
    this.moveComparisonRow(event.currentTarget?.closest("[data-comparison-row]"), "up")
  }

  moveComparisonRowDown(event) {
    event?.preventDefault()
    this.moveComparisonRow(event.currentTarget?.closest("[data-comparison-row]"), "down")
  }

  async moveComparisonRow(row, direction) {
    if (!row || !["up", "down"].includes(direction)) return

    const sibling = direction === "up" ? row.previousElementSibling : row.nextElementSibling
    if (!sibling?.matches?.("[data-comparison-row]")) return

    const moveButton = row.querySelector(direction === "up"
      ? "[data-action='kmong-service-registration-draft#moveComparisonRowUp']"
      : "[data-action='kmong-service-registration-draft#moveComparisonRowDown']")
    const moveUrl = row.dataset.comparisonSourceMoveUrl
    const applyDomMove = () => {
      if (direction === "up") {
        row.parentElement.insertBefore(row, sibling)
      } else {
        row.parentElement.insertBefore(sibling, row)
      }
      this.syncComparisonRowOrderState()
      this.reorderComparisonSourceTabsFromRows()
    }

    if (!row.dataset.comparisonSourceId || !moveUrl) {
      applyDomMove()
      return
    }

    moveButton?.setAttribute("disabled", "disabled")
    try {
      const response = await this.jsonFetch(moveUrl, {
        method: "POST",
        body: JSON.stringify({ direction })
      })
      if (!response.ok) throw new Error(response.error || "비교 대상 순서를 바꾸지 못했습니다.")
      applyDomMove()
      this.markComparisonRowOrderPersisted()
      this.setComparisonProgress("비교 대상 순서를 저장했습니다. URL을 다시 읽지는 않았습니다.", "done")
    } catch (error) {
      this.setComparisonProgress(error?.message || "비교 대상 순서를 바꾸지 못했습니다.", "error")
    } finally {
      this.syncComparisonRowOrderState()
    }
  }

  syncComparisonRowOrderState() {
    const rows = Array.from(this.element.querySelectorAll("[data-comparison-row]"))
    rows.forEach((row, index) => {
      const upButton = row.querySelector("[data-action='kmong-service-registration-draft#moveComparisonRowUp']")
      const downButton = row.querySelector("[data-action='kmong-service-registration-draft#moveComparisonRowDown']")
      if (upButton) upButton.disabled = index === 0 || row.classList.contains("is-running")
      if (downButton) downButton.disabled = index >= rows.length - 1 || row.classList.contains("is-running")
    })
  }

  markComparisonRowOrderPersisted() {
    this.element.querySelectorAll("[data-comparison-row]").forEach((row, index) => {
      if (row.dataset.comparisonSourceId) row.dataset.comparisonOriginalPosition = String(index + 1)
    })
  }

  async updateActiveComparisonSourceUrl(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonSourceUpdateUrl
    const input = this.element.querySelector("[data-active-comparison-source-url-input]")
    const sourceUrl = input?.value?.trim() || ""
    if (!url) return

    if (!sourceUrl) {
      this.flashButton(button, "URL 필요", false)
      this.setComparisonProgress("다시 불러올 크몽 URL을 입력해 주세요.", "error")
      input?.focus()
      return
    }

    const progressRunId = this.startActiveComparisonProgress(button, "다시 읽는 중", button.dataset.comparisonStatusUrl)
    try {
      this.setComparisonProgress("URL을 다시 읽고 분석한 뒤 초안에 반영 중입니다.", "running")
      const sourcePayload = {
        source_role: button.dataset.comparisonSourceRole || "own",
        input_type: "url",
        source_url: sourceUrl,
        position: button.dataset.comparisonSourcePosition || ""
      }
      const response = this.isKmongEditUrl(sourceUrl) && button.dataset.comparisonSourceImportUrl
        ? await this.importComparisonEditSource({
            row: null,
            sourceId: button.dataset.comparisonSourceId || "",
            importUrl: button.dataset.comparisonSourceImportUrl,
            payload: sourcePayload
          }, progressRunId)
        : await this.jsonFetch(url, {
            method: "PATCH",
            signal: this.activeComparisonRequestController?.signal,
            body: JSON.stringify({
              progress_run_id: progressRunId,
              competitor_source: sourcePayload
            })
          })
      if (!response.ok) throw new Error(response.error || "URL을 변경하지 못했습니다.")
      this.renderResponseComparisonProgress(response)
      this.setComparisonProgress("URL 변경과 다시 불러오기가 완료되었습니다. 화면을 새로고침합니다.", "done")
      this.completeActiveComparisonProgress({ restoreButton: true, reload: true })
    } catch (error) {
      if (error?.name === "AbortError") return
      this.failActiveComparisonProgress(error)
    }
  }

  async deleteComparisonSource(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonDeleteUrl
    if (!url) return
    if (!window.confirm("이 비교 대상을 삭제할까요?")) return

    await this.withBusyButton(button, "삭제 중", async () => {
      const response = await this.jsonFetch(url, { method: "DELETE" })
      if (!response.ok) throw new Error(response.error || "비교 대상을 삭제하지 못했습니다.")
      window.location.href = this.comparisonContextHref({ clearSource: true })
    })
  }

  async createComparisonReport(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonReportUrl
    if (!url) return

    const openMode = button.dataset.comparisonReportOpenMode || "new-tab"
    const reportWindow = openMode === "new-tab" ? window.open("about:blank", "_blank") : null
    if (reportWindow) {
      reportWindow.opener = null
    }

    await this.withBusyButton(button, "생성 중", async () => {
      try {
        const analysisMode = button.dataset.comparisonAnalysisMode || "text_only"
        const analyzeImages = analysisMode === "with_images"
        const response = await this.jsonFetch(url, {
          method: "POST",
          body: JSON.stringify({
            model_name: this.currentKlavyAgentModel(),
            text_model_id: this.currentKlavyAgentModel(),
            analyze_images: analyzeImages,
            image_analysis: analysisMode
          })
        })
        const finalResponse = await this.resolveComparisonReportGeneration(response, button)
        const reportHref = this.comparisonReportShowHref(finalResponse.data?.report, button) || finalResponse.data?.report_url
        if (!response.ok) {
          if (reportHref) {
            if (openMode === "same-tab") {
              this.navigateComparisonReportHref(reportHref)
            } else {
              this.openComparisonReportHref(reportHref, reportWindow)
            }
            return
          }
          throw new Error(response.error || "개선 리포트를 생성하지 못했습니다.")
        }
        if (!reportHref) {
          throw new Error("생성된 리포트 주소를 확인하지 못했습니다.")
        }
        if (openMode === "same-tab") {
          this.navigateComparisonReportHref(reportHref)
        } else {
          this.openComparisonReportHref(reportHref, reportWindow)
        }
      } catch (error) {
        if (reportWindow && !reportWindow.closed) reportWindow.close()
        throw error
      }
    }, { restoreOnSuccess: true })
  }

  async resolveComparisonReportGeneration(response, button) {
    const report = response.data?.report || {}
    const statusUrl = response.data?.status_url
    if (!response.ok || !statusUrl || !["queued", "generating"].includes(String(report.status || ""))) {
      return response
    }

    const pollInterval = Number(response.data?.poll_interval_ms) || 2000
    const startedAt = Date.now()
    const timeoutMs = 30 * 60 * 1000
    this.setComparisonProgress("AI 경쟁력 리포트 생성 작업을 시작했습니다.", "running")

    while (Date.now() - startedAt < timeoutMs) {
      await this.sleep(pollInterval)
      const statusResponse = await this.jsonFetch(statusUrl, { method: "GET" })
      const progress = statusResponse.data?.progress || {}
      const message = progress.message || "AI 경쟁력 리포트를 생성하고 있습니다."
      const percent = progress.percent != null ? ` ${progress.percent}%` : ""
      this.setComparisonProgress(`${message}${percent}`, progress.status === "failed" ? "error" : "running")
      if (button) button.textContent = progress.percent != null ? `생성 중 ${progress.percent}%` : "생성 중"

      const status = statusResponse.data?.report?.status
      if (status === "failed") {
        const error = new Error(statusResponse.data?.report?.error_message || progress.message || "개선 리포트를 생성하지 못했습니다.")
        error.reportUrl = statusResponse.data?.report_url
        throw error
      }
      if (statusResponse.data?.terminal && ["ready", "applied"].includes(String(status))) {
        this.setComparisonProgress("AI 경쟁력 리포트 생성이 완료되었습니다.", "done")
        return statusResponse
      }
    }

    throw new Error("AI 경쟁력 리포트 생성 상태 확인 시간이 초과되었습니다. 잠시 후 리포트 화면을 새로고침해 주세요.")
  }

  currentKlavyAgentModel() {
    try {
      return window.localStorage?.getItem("baAgent-model") || null
    } catch (_error) {
      return null
    }
  }

  openComparisonReportPage(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const href = button.dataset.comparisonReportOpenUrl
    if (!href) return

    this.openComparisonReportHref(this.withCurrentSourceParam(href), window.open("about:blank", "_blank"))
  }

  async resetComparisonReports(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonReportResetUrl
    if (!url) return
    if (!window.confirm("생성된 개선 리포트를 초기화할까요? 리포트는 비워지고 다시 생성할 수 있습니다.")) return

    await this.withBusyButton(button, "초기화 중", async () => {
      const response = await this.jsonFetch(this.withCurrentSourceParam(url), { method: "DELETE" })
      if (!response.ok) throw new Error(response.error || "개선 리포트를 초기화하지 못했습니다.")
      this.navigateComparisonReportHref(response.data?.redirect_url || this.withCurrentSourceParam(url.replace(/\/reset$/, "")))
    })
  }

  toggleComparisonApplySections(event) {
    const checked = event.currentTarget.checked
    this.element.querySelectorAll("[data-comparison-apply-section]").forEach((input) => {
      input.checked = checked
    })
    this.syncComparisonApplyAll()
  }

  syncComparisonApplyAll() {
    const allInput = this.element.querySelector("[data-comparison-apply-all]")
    if (!allInput) return

    const sectionInputs = Array.from(this.element.querySelectorAll("[data-comparison-apply-section]"))
    const checkedCount = sectionInputs.filter((input) => input.checked).length
    allInput.checked = sectionInputs.length > 0 && checkedCount === sectionInputs.length
    allInput.indeterminate = checkedCount > 0 && checkedCount < sectionInputs.length
  }

  handleComparisonPreviewRequest(event) {
    const detail = event?.detail || {}
    const sections = Array.isArray(detail.sections) ? detail.sections.map(String).filter(Boolean) : []
    const mode = detail.mode || (sections.length > 0 ? "selected" : "all")

    this.previewComparisonReportSections({ sections, mode }).catch((error) => {
      console.error("[KmongComparison] preview request failed", error)
      window.alert(error?.message || "개선 미리보기를 만들지 못했습니다.")
    })
  }

  handleComparisonDetailImagesRequest() {
    const button = this.element.querySelector("[data-comparison-detail-images-url]")
    if (!button) {
      this.emitComparisonDetailImagesProgress({
        status: "failed",
        message: "상세 이미지 생성 버튼을 찾지 못했습니다."
      })
      window.alert("상세 이미지 생성 버튼을 찾지 못했습니다.")
      return
    }
    this.generateComparisonDetailImages({ preventDefault() {}, currentTarget: button }).catch((error) => {
      console.error("[KmongComparison] detail image generation request failed", error)
      window.alert(error?.message || "상세 이미지 생성을 시작하지 못했습니다.")
    })
  }

  handleComparisonMainImageRequest() {
    const button = this.element.querySelector("[data-comparison-main-image-url]")
    if (!button) {
      window.alert("대표 이미지 생성 버튼을 찾지 못했습니다.")
      return
    }
    this.generateComparisonMainImage({ preventDefault() {}, currentTarget: button }).catch((error) => {
      console.error("[KmongComparison] main image generation request failed", error)
      window.alert(error?.message || "대표 이미지 생성을 시작하지 못했습니다.")
    })
  }

  async applyComparisonReport(event) {
    event?.preventDefault()
    const button = event.currentTarget

    const sections = button.dataset.comparisonApplyMode === "all"
      ? []
      : Array.from(this.element.querySelectorAll("[data-comparison-apply-section]:checked")).map((input) => input.value)

    if (button.dataset.comparisonApplyMode !== "all" && sections.length === 0) {
      this.flashButton(button, "선택 필요", false)
      return
    }

    const previewCreated = await this.previewComparisonReportSections({
      sections,
      mode: button.dataset.comparisonApplyMode === "all" ? "all" : "selected",
      button
    })
    if (previewCreated) this.flashButton(button, "미리보기 완료", true)
  }

  async previewComparisonReportSections({ sections = [], mode = "selected", button = null } = {}) {
    const url = button?.dataset?.comparisonPreviewPatchUrl || this.comparisonPreviewPatchUrl()
    if (!url) return false

    let previewCreated = false
    const work = async () => {
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({ sections, mode })
      })
      if (!response.ok) throw new Error(response.error || "개선 미리보기를 만들지 못했습니다.")
      if (!this.hasComparisonReportImprovedPreviewTarget) return

      this.comparisonReportImprovedPreviewTarget.innerHTML = response.data?.html || ""
      if (this.hasComparisonReportShellTarget) {
        this.comparisonReportShellTarget.dataset.improvedCollapsed = "false"
        this.persistComparisonReportPanelState()
      }
      this.syncComparisonReportPaneControls()
      previewCreated = true
    }

    if (button) {
      await this.withBusyButton(button, "미리보기 중", work, { restoreOnSuccess: true })
    } else {
      await work()
    }
    return previewCreated
  }

  comparisonPreviewPatchUrl() {
    if (!this.hasComparisonReportShellTarget) return null
    return this.comparisonReportShellTarget.dataset.comparisonPreviewPatchUrl || null
  }

  async saveComparisonReport(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonApplyUrl || this.comparisonApplyUrl()
    if (!url) return

    const sections = button.dataset.comparisonApplyMode === "all"
      ? []
      : Array.from(this.element.querySelectorAll("[data-comparison-apply-section]:checked")).map((input) => input.value)

    if (button.dataset.comparisonApplyMode !== "all" && sections.length === 0) {
      this.flashButton(button, "선택 필요", false)
      return
    }

    const confirmMessage = button.dataset.comparisonApplyMode === "all"
      ? "리포트의 전체 개선안을 현재 초안에 저장 반영할까요?"
      : "선택한 개선안을 현재 초안에 저장 반영할까요?"
    if (!window.confirm(confirmMessage)) return

    await this.withBusyButton(button, "저장 중", async () => {
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({ sections })
      })
      if (!response.ok) throw new Error(response.error || "개선안을 저장 반영하지 못했습니다.")
      window.location.reload()
    })
  }

  async generateComparisonDetailImages(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonDetailImagesUrl
    if (!url) return

    const imageModel = this.currentKlavyAgentImageModel()
    if (!imageModel) {
      this.flashButton(button, "모델 선택 필요", false)
      this.setComparisonDetailImagesStatus("이미지 생성 모델을 선택해주세요.", "error")
      this.emitComparisonDetailImagesProgress({
        status: "failed",
        message: "이미지 생성 모델을 선택해주세요."
      })
      return
    }

    await this.withBusyButton(button, "상세 이미지 생성 중", async () => {
      this.emitComparisonDetailImagesProgress({
        status: "running",
        message: "상세 이미지 생성 요청을 보내고 있습니다.",
        details: { phase: "request", elapsed_seconds: 0 }
      })
      const requestStartedAt = Date.now()
      const requestWaitingTimer = window.setInterval(() => {
        const elapsedSeconds = Math.max(1, Math.floor((Date.now() - requestStartedAt) / 1000))
        this.emitComparisonDetailImagesProgress({
          status: "running",
          message: `상세 이미지 생성 요청 전송 중 · 서버 응답 대기 ${elapsedSeconds}초`,
          details: { phase: "request", elapsed_seconds: elapsedSeconds }
        })
      }, 1000)
      let response
      try {
        response = await this.jsonFetch(url, {
          method: "POST",
          body: JSON.stringify({ image_model_id: imageModel })
        })
      } finally {
        window.clearInterval(requestWaitingTimer)
      }
      if (!response.ok) throw new Error(response.error || "상세 이미지 생성 작업을 시작하지 못했습니다.")

      this.emitComparisonDetailImagesProgress(response.data?.detail_image_generation_progress)
      const finalData = await this.pollComparisonDetailImages(response.data?.status_url, response.data?.poll_interval_ms || 2000)
      this.renderComparisonDetailImages(finalData?.generated_images)
      this.setComparisonDetailImagesStatus(finalData?.detail_image_generation_progress?.message || "상세 이미지 생성이 완료되었습니다.", "done")
      if (this.generatedComparisonDetailImageCount(finalData?.generated_images) > 0) {
        this.enableComparisonGeneratedDetailApply()
      }
    }, { restoreOnSuccess: true })
  }

  async generateComparisonMainImage(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonMainImageUrl
    if (!url) return

    const imageModel = this.currentKlavyAgentImageModel()
    if (!imageModel) {
      this.flashButton(button, "모델 선택 필요", false)
      this.setComparisonMainImageStatus("이미지 생성 모델을 선택해주세요.", "error")
      return
    }

    await this.withBusyButton(button, "대표 이미지 생성 중", async () => {
      this.setComparisonMainImageStatus("대표 이미지 생성 요청을 보내고 있습니다.", "running")
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({
          image_model_id: imageModel,
          role: "main",
          main_image_mode: button.dataset.comparisonMainImageMode || "product"
        })
      })
      if (!response.ok) throw new Error(response.error || "대표 이미지 생성 작업을 시작하지 못했습니다.")

      const generated = response.data?.generated_image || response.data?.generated_images?.main
      this.renderComparisonMainImage(generated)
      this.setComparisonMainImageStatus("대표 이미지 후보를 생성했습니다.", "done")
      if (generated) this.enableComparisonGeneratedMainApply()
    }, { restoreOnSuccess: true })
  }

  async pollComparisonDetailImages(statusUrl, pollIntervalMs = 2000) {
    if (!statusUrl) throw new Error("상세 이미지 상태 확인 주소를 찾지 못했습니다.")

    const startedAt = Date.now()
    const timeoutMs = 30 * 60 * 1000
    while (Date.now() - startedAt < timeoutMs) {
      await this.sleep(pollIntervalMs)
      const response = await this.jsonFetch(statusUrl, { method: "GET" })
      if (!response.ok) throw new Error(response.error || "상세 이미지 생성 상태를 확인하지 못했습니다.")

      const data = response.data || {}
      const progress = data.detail_image_generation_progress || {}
      const message = progress.message || "상세 이미지를 생성하고 있습니다."
      const percent = progress.percent != null ? ` ${progress.percent}%` : ""
      this.setComparisonDetailImagesStatus(`${message}${percent}`, progress.status === "failed" ? "error" : "running")
      this.emitComparisonDetailImagesProgress(progress)

      if (["done", "partial"].includes(String(progress.status || ""))) return data
      if (String(progress.status || "") === "failed") {
        throw new Error(progress.message || "상세 이미지 생성에 실패했습니다.")
      }
    }

    throw new Error("상세 이미지 생성 상태 확인 시간이 초과되었습니다. 잠시 후 새로고침해 주세요.")
  }

  async applyComparisonGeneratedDetailImages(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonApplyGeneratedDetailImagesUrl || button.dataset.comparisonApplyGeneratedImagesUrl
    if (!url) return
    if (!window.confirm("생성된 상세 이미지를 현재 초안 상세 이미지로 저장할까요? 기존 상세 이미지는 교체됩니다.")) return

    await this.withBusyButton(button, "적용 중", async () => {
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({ roles: ["detail"] })
      })
      if (!response.ok) throw new Error(response.error || "상세 이미지를 초안에 적용하지 못했습니다.")
      window.location.reload()
    })
  }

  async applyComparisonGeneratedMainImage(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.comparisonApplyGeneratedMainImageUrl
    if (!url) return
    if (!window.confirm("생성된 대표 이미지를 현재 초안 대표 이미지로 저장할까요? 기존 대표 이미지는 교체됩니다.")) return

    await this.withBusyButton(button, "적용 중", async () => {
      const response = await this.jsonFetch(url, {
        method: "POST",
        body: JSON.stringify({ roles: ["main"] })
      })
      if (!response.ok) throw new Error(response.error || "대표 이미지를 초안에 적용하지 못했습니다.")
      window.location.reload()
    })
  }

  currentKlavyAgentImageModel() {
    try {
      return window.localStorage?.getItem("baAgent_kmong-service-writer-threads-image-model") ||
        window.localStorage?.getItem("baAgent-threads-image-model") ||
        ""
    } catch (_error) {
      return ""
    }
  }

  setComparisonDetailImagesStatus(message, state = "running") {
    const el = this.element.querySelector("[data-comparison-detail-images-status]")
    if (!el) return

    el.textContent = message
    el.dataset.state = state
  }

  setComparisonMainImageStatus(message, state = "running") {
    const el = this.element.querySelector("[data-comparison-main-image-status]")
    if (!el) return

    el.textContent = message
    el.dataset.state = state
  }

  renderComparisonMainImage(image) {
    const list = this.element.querySelector("[data-comparison-main-image-list]")
    if (!list) return

    if (!image) {
      list.innerHTML = ""
      list.hidden = true
      return
    }

    const url = this.escapeHtml(image.url || "")
    const alt = this.escapeHtml(image.alt_text || "생성 대표 이미지")
    const title = this.escapeHtml(image.title || "대표 이미지 후보")
    const imageTag = url ? `<img src="${url}" alt="${alt}" class="kcr2__media-thumb kcr2__media-thumb--wide">` : ""
    list.innerHTML = `<div class="kcr2__media-item">${imageTag}<span>생성 대표 이미지</span><strong>${title}</strong></div>`
    list.hidden = false
  }

  renderComparisonDetailImages(generatedImages) {
    const list = this.element.querySelector("[data-comparison-detail-images-list]")
    if (!list) return

    const details = Array.isArray(generatedImages?.details) ? generatedImages.details : []
    list.innerHTML = details.map((image, index) => {
      const url = this.escapeHtml(image.url || "")
      const alt = this.escapeHtml(image.alt_text || `생성 상세 이미지 ${index + 1}`)
      const title = this.escapeHtml(image.title || `상세 이미지 ${index + 1}`)
      const position = this.escapeHtml(image.position || index + 1)
      const imageTag = url ? `<img src="${url}" alt="${alt}" class="kcr2__media-thumb">` : ""
      return `<div class="kcr2__media-item">${imageTag}<span>생성 상세 이미지 ${position}</span><strong>${title}</strong></div>`
    }).join("")
    list.hidden = details.length === 0
  }

  enableComparisonGeneratedDetailApply() {
    const button = this.element.querySelector("[data-comparison-apply-generated-detail-images-url]")
    if (button) button.disabled = false
  }

  enableComparisonGeneratedMainApply() {
    const button = this.element.querySelector("[data-comparison-apply-generated-main-image-url]")
    if (button) button.disabled = false
  }

  emitComparisonDetailImagesProgress(progress = {}) {
    window.dispatchEvent(new CustomEvent("kmong-comparison-detail-images-progress", {
      detail: progress || {}
    }))
  }

  generatedComparisonDetailImageCount(generatedImages) {
    return Array.isArray(generatedImages?.details) ? generatedImages.details.length : 0
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  comparisonApplyUrl() {
    if (!this.hasComparisonReportShellTarget) return null
    return this.comparisonReportShellTarget.dataset.comparisonApplyUrl || null
  }

  async restoreDraftVersion(event) {
    event?.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.versionRestoreUrl
    if (!url) return
    if (!window.confirm("선택한 이전 버전으로 현재 초안을 복원할까요?")) return

    await this.withBusyButton(button, "복원 중", async () => {
      const response = await this.jsonFetch(url, { method: "POST" })
      if (!response.ok) throw new Error(response.error || "버전을 복원하지 못했습니다.")
      window.location.reload()
    })
  }

  replaceComparisonValueControl(row) {
    if (!row) return
    const type = row.querySelector("[data-comparison-input-type]")?.dataset.value || "url"
    const current = row.querySelector("[data-comparison-value]")
    if (!current) return

    const replacement = document.createElement(type === "text" ? "textarea" : "input")
    replacement.dataset.comparisonValue = "true"
    replacement.value = current.value || ""
    replacement.placeholder = type === "text" ? "경쟁 상세 페이지 내용을 붙여넣으세요" : "https://kmong.com/..."
    replacement.autocomplete = "off"
    if (type === "url") replacement.type = "url"
    current.replaceWith(replacement)
  }

  comparisonSelectHtml(dataAttr, value, options) {
    const label = (options.find((option) => option[0] === value) || options[0])[1]
    const optionHtml = options.map(([optionValue, optionLabel]) => `
      <button type="button" class="ksrd-comparison-select__option" role="option" aria-selected="${optionValue === value ? "true" : "false"}" data-option-value="${optionValue}" data-option-label="${optionLabel}" data-action="kmong-service-registration-draft#chooseComparisonOption">
        <span>${optionLabel}</span>
        <svg class="ksrd-comparison-select__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"></path></svg>
      </button>
    `).join("")
    return `
      <div class="ksrd-comparison-select" ${dataAttr} data-value="${value}">
        <button type="button" class="ksrd-comparison-select__trigger" data-action="kmong-service-registration-draft#toggleComparisonSelect" aria-haspopup="listbox" aria-expanded="false">
          <span data-comparison-select-label>${label}</span>
          <svg class="ksrd-comparison-select__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"></path></svg>
        </button>
        <div class="ksrd-comparison-select__menu" role="listbox" hidden>${optionHtml}</div>
      </div>
    `
  }

  comparisonRowProgressHtml() {
    return `
      <div class="ksrd-comparison-row-progress" data-comparison-row-progress data-progress-status="pending" data-progress-started-at="">
        <div class="ksrd-comparison-row-progress__message" data-comparison-row-progress-message>대기 중입니다.</div>
        <div class="ksrd-source-progress__sections">
          ${[
            ["details", "상세 정보"],
            ["media", "이미지와 동영상"],
            ["pricing", "가격 정보"],
            ["sales_core", "판매 핵심 정보"],
            ["profile", "전문가 프로필"],
            ["snapshot", "완료 저장"]
          ].map(([key, label]) => `
            <div class="ksrd-source-progress__section is-pending" data-comparison-row-section="${key}">
              <div>
                <span>${label}</span>
                <small data-comparison-row-section-message>대기 중</small>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
    `
  }

  comparisonRowOrderHtml() {
    return `
      <div class="ksrd-comparison-row__order" aria-label="비교 대상 순서">
        <button type="button" class="ksrd-comparison-row__move" data-action="kmong-service-registration-draft#moveComparisonRowUp" aria-label="위로 이동" title="위로 이동">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2"><path d="m18 15-6-6-6 6"></path></svg>
        </button>
        <button type="button" class="ksrd-comparison-row__move" data-action="kmong-service-registration-draft#moveComparisonRowDown" aria-label="아래로 이동" title="아래로 이동">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2"><path d="m6 9 6 6 6-6"></path></svg>
        </button>
      </div>
    `
  }

  comparisonContextHref(options = {}) {
    const url = new URL(window.location.href)
    url.searchParams.delete("comparison")
    if (options.clearSource) url.searchParams.delete("source")
    if (this.activeTabValue === "profile") {
      url.searchParams.set("tab", "profile")
    } else {
      url.searchParams.delete("tab")
    }
    if (options.hash) url.hash = options.hash
    return `${url.pathname}${url.search}${url.hash}`
  }

  navigateOrReload(href) {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (href === current) {
      window.location.reload()
    } else {
      window.location.href = href
    }
  }

  sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
  }

  comparisonReportShowHref(report, button) {
    const explicitHref = report?.show_url || report?.url || report?.href
    if (explicitHref) return this.withCurrentSourceParam(explicitHref)

    const reportId = report?.id
    const template = button?.dataset?.comparisonReportShowUrlTemplate
    if (reportId && template) return this.withCurrentSourceParam(template.replace("__REPORT_ID__", encodeURIComponent(reportId)))

    const collectionUrl = button?.dataset?.comparisonReportUrl
    if (!reportId || !collectionUrl) return null

    const baseUrl = new URL(collectionUrl, window.location.origin)
    const pathname = baseUrl.pathname.replace(/\/$/, "")
    baseUrl.pathname = `${pathname}/${encodeURIComponent(reportId)}`
    return this.withCurrentSourceParam(`${baseUrl.pathname}${baseUrl.search}${baseUrl.hash}`)
  }

  withCurrentSourceParam(href) {
    const sourceId = new URL(window.location.href).searchParams.get("source")
    if (!sourceId) return href

    const url = new URL(href, window.location.origin)
    if (!url.searchParams.has("source")) url.searchParams.set("source", sourceId)
    return `${url.pathname}${url.search}${url.hash}`
  }

  openComparisonReportHref(href, reportWindow) {
    const targetHref = new URL(href, window.location.origin).toString()

    if (reportWindow && !reportWindow.closed) {
      reportWindow.location.href = targetHref
      reportWindow.focus()
      return
    }

    const openedWindow = window.open(targetHref, "_blank")
    if (openedWindow) {
      openedWindow.opener = null
      openedWindow.focus()
      return
    }

    throw new Error("브라우저가 새 탭 열기를 차단했습니다. 팝업 허용 후 다시 생성해 주세요.")
  }

  navigateComparisonReportHref(href) {
    window.location.href = new URL(href, window.location.origin).toString()
  }

  comparisonEntryChanged(entry) {
    const row = entry?.row
    if (!row) return false

    const originalRole = row.dataset.comparisonOriginalRole || ""
    const originalInputType = row.dataset.comparisonOriginalInputType || ""
    const originalValue = row.dataset.comparisonOriginalValue || ""
    const currentValue = entry.payload.input_type === "url" ? entry.payload.source_url : entry.payload.raw_text

    return originalRole !== entry.payload.source_role ||
      originalInputType !== entry.payload.input_type ||
      originalValue.trim() !== currentValue.trim()
  }

  syncComparisonSourceUrl(sourceId, reload = false) {
    if (!window.history?.replaceState) return

    const url = new URL(window.location.href)
    url.searchParams.set("source", sourceId)
    url.searchParams.delete("comparison")
    const nextLocation = `${url.pathname}${url.search}${url.hash}`
    if (reload) {
      window.location.href = nextLocation
    } else {
      window.history.replaceState(window.history.state, "", nextLocation)
    }
  }

  setComparisonRowStatus(row, message, state = "pending") {
    const status = row?.querySelector("[data-comparison-row-status]")
    if (!status) return

    status.textContent = message
    status.classList.toggle("is-running", state === "running")
    status.classList.toggle("is-done", state === "done")
    status.classList.toggle("is-error", state === "error")
    row.classList.toggle("is-running", state === "running")
  }

  attachComparisonRowSource(row, source) {
    if (!row || !source) return

    if (source.id) row.dataset.comparisonSourceId = source.id
    if (source.update_url) row.dataset.comparisonSourceUpdateUrl = source.update_url
    if (source.move_url) row.dataset.comparisonSourceMoveUrl = source.move_url
    if (source.delete_url) row.dataset.comparisonSourceDeleteUrl = source.delete_url
    if (source.cancel_url) row.dataset.comparisonSourceCancelUrl = source.cancel_url
    if (source.import_url) row.dataset.comparisonSourceImportUrl = source.import_url
    if (source.status_url) row.dataset.comparisonSourceStatusUrl = source.status_url
    if (source.position) row.dataset.comparisonOriginalPosition = String(source.position)
    this.syncComparisonRowOrderState()
  }

  startComparisonRowProgress(row, statusUrl) {
    const runId = this.generateRunId()
    if (!row) return runId

    row.dataset.comparisonProgressRunId = runId
    this.stopComparisonRowPolling(row)
    this.resetComparisonRowProgressPanel(row, "비교 대상 읽기를 준비합니다.")
    const panel = row.querySelector("[data-comparison-row-progress]")
    if (panel) {
      panel.dataset.progressStatus = "running"
      panel.dataset.progressStartedAt = new Date().toISOString()
    }

    if (statusUrl) {
      const timer = window.setInterval(() => this.pollComparisonRowProgress(row), 2000)
      this.comparisonRowPollTimers.set(row, timer)
      window.setTimeout(() => this.pollComparisonRowProgress(row), 250)
    }

    return runId
  }

  resumeComparisonRowProgress() {
    this.element.querySelectorAll("[data-comparison-row]").forEach((row) => {
      const panel = row.querySelector("[data-comparison-row-progress]")
      const runId = row.dataset.comparisonProgressRunId
      const statusUrl = row.dataset.comparisonSourceStatusUrl
      if (!panel || !runId || !statusUrl) return
      if (panel.dataset.progressStatus !== "running") return
      if (this.comparisonRowPollTimers.has(row)) return

      const timer = window.setInterval(() => this.pollComparisonRowProgress(row), 2000)
      this.comparisonRowPollTimers.set(row, timer)
      window.setTimeout(() => this.pollComparisonRowProgress(row), 250)
    })
  }

  resetComparisonRowProgressPanel(row, message) {
    const panel = row?.querySelector("[data-comparison-row-progress]")
    if (!panel) return

    panel.dataset.progressStatus = "pending"
    panel.dataset.progressStartedAt = ""
    const messageNode = panel.querySelector("[data-comparison-row-progress-message]")
    if (messageNode) messageNode.textContent = message || "대기 중입니다."
    panel.querySelectorAll("[data-comparison-row-section]").forEach((section) => {
      this.setComparisonRowSection(section, "pending", "대기 중")
    })
  }

  async pollComparisonRowProgress(row) {
    const runId = row?.dataset?.comparisonProgressRunId
    const statusUrl = row?.dataset?.comparisonSourceStatusUrl
    if (!row || !runId || !statusUrl) return
    if (this.comparisonRowPollInFlight?.has(row)) return

    this.comparisonRowPollInFlight.add(row)
    let response
    try {
      const url = new URL(statusUrl, window.location.origin)
      url.searchParams.set("progress_run_id", runId)
      response = await this.jsonFetch(url.toString(), { method: "GET" })
    } catch (_error) {
      return
    } finally {
      this.comparisonRowPollInFlight.delete(row)
    }

    if (row.dataset.comparisonProgressRunId !== runId) {
      this.stopComparisonRowPolling(row)
      return
    }

    if (!response.ok) return
    if (response.data?.stale) {
      this.stopComparisonRowPolling(row)
      const panel = row.querySelector("[data-comparison-row-progress]")
      if (panel) panel.dataset.progressStatus = "failed"
      this.setComparisonRowStatus(row, "다른 저장 작업으로 대체되었습니다.", "error")
      return
    }

    const progress = response.data?.progress || {}
    this.renderComparisonRowProgress(row, progress)
    if (["done", "failed", "cancelled"].includes(progress.status)) {
      this.stopComparisonRowPolling(row)
    }
  }

  renderResponseComparisonRowProgress(row, response) {
    const progress = response?.data?.source?.metadata?.refresh_progress
    if (progress) this.renderComparisonRowProgress(row, progress)
  }

  renderComparisonRowProgress(row, progress) {
    const panel = row?.querySelector("[data-comparison-row-progress]")
    if (!panel) return

    if (progress.started_at) panel.dataset.progressStartedAt = progress.started_at
    if (progress.status) panel.dataset.progressStatus = progress.status
    const messageNode = panel.querySelector("[data-comparison-row-progress-message]")
    if (messageNode) messageNode.textContent = progress.message || "진행 중입니다."

    Object.entries(progress.sections || {}).forEach(([key, section]) => {
      const node = panel.querySelector(`[data-comparison-row-section="${key}"]`)
      if (node) this.setComparisonRowSection(node, section.status || "pending", section.message || "대기 중")
    })

    if (progress.status === "running") {
      this.setComparisonRowStatus(row, progress.message || "진행 중입니다.", "running")
    } else if (progress.status === "done") {
      this.setComparisonRowStatus(row, progress.message || "완료: URL 읽기와 분석을 마쳤습니다.", "done")
    } else if (progress.status === "failed") {
      this.setComparisonRowStatus(row, progress.error || progress.message || "등록 실패", "error")
    } else if (progress.status === "cancelled") {
      this.setComparisonRowStatus(row, progress.message || "사용자가 중단했습니다.", "error")
    }
  }

  setComparisonRowSection(section, status, message) {
    section.classList.remove("is-pending", "is-running", "is-done", "is-skipped", "is-failed")
    section.classList.add(`is-${status || "pending"}`)
    const messageNode = section.querySelector("[data-comparison-row-section-message]")
    if (messageNode) messageNode.textContent = message || "대기 중"
  }

  stopComparisonRowPolling(row) {
    const timer = this.comparisonRowPollTimers?.get(row)
    if (timer) window.clearInterval(timer)
    this.comparisonRowPollTimers?.delete(row)
    this.comparisonRowPollInFlight?.delete(row)
  }

  stopComparisonRowPollTimers() {
    if (!this.comparisonRowPollTimers) return
    this.comparisonRowPollTimers.forEach((timer) => window.clearInterval(timer))
    this.comparisonRowPollTimers.clear()
    this.comparisonRowPollInFlight?.clear()
  }

  waitForComparisonRows(rows, timeoutMs = 20 * 60 * 1000) {
    const pendingRows = rows.filter((row) => row?.querySelector("[data-comparison-row-progress]"))
    if (pendingRows.length === 0) return Promise.resolve({ done: 0, failed: 0, timedOut: 0 })

    const startedAt = Date.now()
    return new Promise((resolve) => {
      this.comparisonSaveWatchResolve = resolve
      this.comparisonSaveWatchTimer = window.setInterval(() => {
        const counts = pendingRows.reduce((memo, row) => {
          const status = row.querySelector("[data-comparison-row-progress]")?.dataset?.progressStatus
          if (status === "done") memo.done += 1
          if (status === "failed") memo.failed += 1
          if (status === "cancelled") memo.cancelled += 1
          return memo
        }, { done: 0, failed: 0, cancelled: 0 })

        const completeCount = counts.done + counts.failed + counts.cancelled
        const timedOut = Date.now() - startedAt > timeoutMs
        if (completeCount < pendingRows.length && !timedOut) return

        this.stopComparisonSaveWatch()
        resolve({
          ...counts,
          timedOut: timedOut ? pendingRows.length - completeCount : 0
        })
      }, 500)
    })
  }

  stopComparisonSaveWatch(result = null) {
    if (this.comparisonSaveWatchTimer) window.clearInterval(this.comparisonSaveWatchTimer)
    this.comparisonSaveWatchTimer = null
    if (result && this.comparisonSaveWatchResolve) this.comparisonSaveWatchResolve(result)
    this.comparisonSaveWatchResolve = null
  }

  cancelComparisonSave() {
    this.comparisonSaveCancelled = true
    this.comparisonSaveInFlight = false
    this.stopComparisonSaveWatch({ cancelled: true, done: 0, failed: 0, timedOut: 0 })
    this.stopComparisonRowPollTimers()
    this.setComparisonModalLocked(false)
    this.restoreComparisonSaveButtons()
    this.startFreshnessPolling()
    this.setComparisonProgress("비교 등록을 중단했습니다.", "error")

    const rows = Array.from(this.element.querySelectorAll("[data-comparison-row]"))
    rows.forEach((row) => {
      const panel = row.querySelector("[data-comparison-row-progress]")
      if (panel?.dataset?.progressStatus === "running" || panel?.dataset?.progressStatus === "pending") {
        panel.dataset.progressStatus = "cancelled"
        const messageNode = panel.querySelector("[data-comparison-row-progress-message]")
        if (messageNode) messageNode.textContent = "사용자가 중단했습니다."
        this.setComparisonRowStatus(row, "사용자가 중단했습니다.", "error")
        this.cancelComparisonRowProcessing(row)
      }
    })
  }

  async cancelComparisonRowProcessing(row) {
    const cancelUrl = row?.dataset?.comparisonSourceCancelUrl
    const progressRunId = row?.dataset?.comparisonProgressRunId
    if (!cancelUrl || !progressRunId) return

    try {
      await this.jsonFetch(cancelUrl, {
        method: "POST",
        body: JSON.stringify({ progress_run_id: progressRunId })
      })
    } catch (_error) {
      // Best effort: closing the modal should not be blocked by cancellation transport errors.
    }
  }

  restoreComparisonSaveButtons() {
    this.element.querySelectorAll("[data-comparison-save-button]").forEach((button) => {
      button.disabled = false
      delete button.dataset.comparisonSaveComplete
      button.textContent = button.dataset.comparisonSaveMode === "all" ? "전체 다시 읽고 저장" : "추가/변경만 저장"
    })
  }

  setComparisonProgress(message, state = "pending") {
    const progress = this.element.querySelector("[data-comparison-progress]")
    if (!progress) return

    progress.textContent = message
    progress.classList.toggle("is-running", state === "running")
    progress.classList.toggle("is-done", state === "done")
    progress.classList.toggle("is-error", state === "error")
  }

  startActiveComparisonProgress(button, loadingText, statusUrl) {
    const runId = this.generateRunId()
    this.stopActiveComparisonProgress({ restoreButton: true })
    this.activeComparisonProgressRunId = runId
    this.activeComparisonProgressStartedAt = Date.now()
    this.activeComparisonProgressButton = button
    this.activeComparisonProgressOriginalText = button?.textContent
    this.activeComparisonRequestController = typeof AbortController === "undefined" ? null : new AbortController()

    if (button) {
      button.disabled = true
      button.textContent = loadingText
    }

    this.resetActiveComparisonProgressPanel("다시 읽기를 준비합니다.")
    this.updateActiveComparisonElapsed()
    this.activeComparisonElapsedTimer = window.setInterval(() => this.updateActiveComparisonElapsed(), 1000)

    if (statusUrl) {
      this.activeComparisonPollTimer = window.setInterval(() => this.pollActiveComparisonProgress(statusUrl), 2000)
      window.setTimeout(() => this.pollActiveComparisonProgress(statusUrl), 250)
    }

    return runId
  }

  resumeActiveComparisonProgress() {
    const panel = this.element.querySelector("[data-active-comparison-progress]")
    if (!panel || panel.hidden) return
    if (panel.dataset.progressStatus && panel.dataset.progressStatus !== "running") return

    const startedAt = Date.parse(panel.dataset.progressStartedAt || "")
    if (!Number.isFinite(startedAt)) return

    this.activeComparisonProgressStartedAt = startedAt
    this.updateActiveComparisonElapsed()
  }

  resetActiveComparisonProgressPanel(message) {
    const panel = this.element.querySelector("[data-active-comparison-progress]")
    if (!panel) return

    panel.hidden = false
    panel.dataset.progressStatus = "running"
    panel.dataset.progressStartedAt = new Date().toISOString()
    const messageNode = panel.querySelector("[data-active-comparison-progress-message]")
    if (messageNode) messageNode.textContent = message
    panel.querySelectorAll("[data-active-comparison-section]").forEach((section) => {
      this.setActiveComparisonSection(section, "pending", "대기 중")
    })
  }

  async pollActiveComparisonProgress(statusUrl) {
    if (!this.activeComparisonProgressRunId) return
    if (this.activeComparisonPollInFlight) return

    this.activeComparisonPollInFlight = true
    let response
    try {
      const url = new URL(statusUrl, window.location.origin)
      url.searchParams.set("progress_run_id", this.activeComparisonProgressRunId)
      response = await this.jsonFetch(url.toString(), { method: "GET" })
    } catch (_error) {
      return
    } finally {
      this.activeComparisonPollInFlight = false
    }

    if (!response.ok || response.data?.stale) return

    const progress = response.data?.progress || {}
    this.renderActiveComparisonProgress(progress)
    if (["done", "failed"].includes(progress.status)) {
      if (progress.status === "done") {
        this.completeActiveComparisonProgress({ restoreButton: true, abortRequest: true, reload: true })
      } else {
        this.completeActiveComparisonProgress({ restoreButton: true })
      }
    }
  }

  renderActiveComparisonProgress(progress) {
    const panel = this.element.querySelector("[data-active-comparison-progress]")
    if (!panel) return

    panel.hidden = false
    if (progress.started_at) panel.dataset.progressStartedAt = progress.started_at
    if (progress.status) panel.dataset.progressStatus = progress.status
    const messageNode = panel.querySelector("[data-active-comparison-progress-message]")
    if (messageNode) messageNode.textContent = progress.message || "진행 중입니다."

    Object.entries(progress.sections || {}).forEach(([key, section]) => {
      const node = panel.querySelector(`[data-active-comparison-section="${key}"]`)
      if (node) this.setActiveComparisonSection(node, section.status || "pending", section.message || "대기 중")
    })

    const startedAt = Date.parse(progress.started_at || "")
    if (Number.isFinite(startedAt)) {
      this.activeComparisonProgressStartedAt = startedAt
      this.updateActiveComparisonElapsed()
    }
  }

  renderResponseComparisonProgress(response) {
    const progress = response?.data?.source?.metadata?.refresh_progress
    if (progress) this.renderActiveComparisonProgress(progress)
  }

  setActiveComparisonSection(section, status, message) {
    section.classList.remove("is-pending", "is-running", "is-done", "is-skipped", "is-failed")
    section.classList.add(`is-${status || "pending"}`)
    const messageNode = section.querySelector("[data-active-comparison-section-message]")
    if (messageNode) messageNode.textContent = message || "대기 중"
  }

  updateActiveComparisonElapsed() {
    const elapsed = this.element.querySelector("[data-active-comparison-elapsed]")
    if (!elapsed || !this.activeComparisonProgressStartedAt) return

    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - this.activeComparisonProgressStartedAt) / 1000))
    elapsed.textContent = this.formatElapsedTime(elapsedSeconds)
  }

  failActiveComparisonProgress(error) {
    this.stopActiveComparisonPolling()
    this.restoreActiveComparisonButton()
    this.activeComparisonRequestController = null
    const panel = this.element.querySelector("[data-active-comparison-progress]")
    const message = error?.message || "다시 읽기에 실패했습니다."
    const messageNode = panel?.querySelector("[data-active-comparison-progress-message]")
    if (messageNode) messageNode.textContent = message
  }

  completeActiveComparisonProgress({ restoreButton = true, abortRequest = false, reload = false } = {}) {
    this.stopActiveComparisonPolling()
    if (abortRequest) this.activeComparisonRequestController?.abort()
    this.activeComparisonRequestController = null
    if (restoreButton) this.restoreActiveComparisonButton()
    this.activeComparisonProgressRunId = null
    this.hasExternalUpdate = true
    this.stopFreshnessPolling()
    if (reload) this.scheduleComparisonReload()
  }

  scheduleComparisonReload() {
    if (this.comparisonReloadScheduled) return

    this.comparisonReloadScheduled = true
    this.hasExternalUpdate = true
    this.stopFreshnessPolling()
    window.setTimeout(() => this.reloadComparisonContext(), 1200)
  }

  reloadComparisonContext() {
    const nextLocation = this.comparisonContextHref()
    const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (nextLocation === currentLocation) {
      window.location.reload()
    } else {
      window.location.href = nextLocation
    }
  }

  stopActiveComparisonProgress({ restoreButton = true } = {}) {
    this.stopActiveComparisonPolling()
    if (restoreButton) this.restoreActiveComparisonButton()
    this.activeComparisonRequestController = null
    this.activeComparisonProgressRunId = null
    this.activeComparisonProgressStartedAt = null
  }

  stopActiveComparisonPolling() {
    if (this.activeComparisonPollTimer) window.clearInterval(this.activeComparisonPollTimer)
    if (this.activeComparisonElapsedTimer) window.clearInterval(this.activeComparisonElapsedTimer)
    this.activeComparisonPollTimer = null
    this.activeComparisonElapsedTimer = null
    this.activeComparisonPollInFlight = false
  }

  restoreActiveComparisonButton() {
    if (!this.activeComparisonProgressButton) return

    this.activeComparisonProgressButton.disabled = false
    if (this.activeComparisonProgressOriginalText) {
      this.activeComparisonProgressButton.textContent = this.activeComparisonProgressOriginalText
    }
    this.activeComparisonProgressButton = null
    this.activeComparisonProgressOriginalText = null
  }

  generateRunId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()

    return `progress-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  setComparisonModalLocked(locked) {
    this.element.querySelectorAll("[data-comparison-modal] input, [data-comparison-modal] textarea, [data-comparison-modal] button").forEach((control) => {
      if (control.dataset.comparisonModalClose === "true") return
      control.disabled = locked
    })
  }

  openComparisonSelect(select) {
    const menu = select.querySelector(".ksrd-comparison-select__menu")
    const trigger = select.querySelector(".ksrd-comparison-select__trigger")
    select.classList.add("is-open")
    if (menu) menu.hidden = false
    if (trigger) trigger.setAttribute("aria-expanded", "true")
  }

  closeComparisonSelect(select) {
    const menu = select.querySelector(".ksrd-comparison-select__menu")
    const trigger = select.querySelector(".ksrd-comparison-select__trigger")
    select.classList.remove("is-open")
    if (menu) menu.hidden = true
    if (trigger) trigger.setAttribute("aria-expanded", "false")
  }

  closeComparisonSelects(except = null) {
    this.element.querySelectorAll(".ksrd-comparison-select.is-open").forEach((select) => {
      if (select !== except) this.closeComparisonSelect(select)
    })
  }

  closeComparisonSelectOnOutsideClick(event) {
    if (event.target.closest(".ksrd-comparison-select")) return
    this.closeComparisonSelects()
  }

  closeComparisonSelectOnEscape(event) {
    if (event.key !== "Escape") return
    this.closeComparisonSelects()
  }

  async jsonFetch(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        ...(options.headers || {})
      },
      ...options
    })
    const payload = await response.json().catch(() => ({}))
    return { ok: response.ok && payload.ok !== false, status: response.status, ...payload }
  }

  async withBusyButton(button, loadingText, callback, options = {}) {
    const isIconButton = button.hasAttribute("data-icon-button")
    const originalText = isIconButton ? button.getAttribute("aria-label") : button.textContent
    const startedAt = Date.now()
    const updateElapsedText = () => {
      const elapsedSeconds = Math.max(1, Math.floor((Date.now() - startedAt) / 1000))
      if (isIconButton) {
        button.setAttribute("aria-label", `${loadingText} ${this.formatElapsedTime(elapsedSeconds)}`)
      } else {
        button.textContent = `${loadingText} ${this.formatElapsedTime(elapsedSeconds)}`
      }
    }

    button.disabled = true
    button.dataset.busy = "true"
    if (isIconButton) {
      button.setAttribute("aria-label", loadingText)
    } else {
      button.textContent = loadingText
    }
    const elapsedTimer = window.setInterval(updateElapsedText, 1000)
    let succeeded = false

    try {
      await callback()
      succeeded = true
    } catch (error) {
      window.clearInterval(elapsedTimer)
      button.disabled = false
      delete button.dataset.busy
      if (isIconButton) {
        button.setAttribute("aria-label", error.message || "실패")
      } else {
        button.textContent = error.message || "실패"
      }
      window.setTimeout(() => {
        if (isIconButton) {
          button.setAttribute("aria-label", originalText || "비교 대상 삭제")
        } else {
          button.textContent = originalText
        }
      }, 1600)
    } finally {
      window.clearInterval(elapsedTimer)
      delete button.dataset.busy
      if (succeeded && options.restoreOnSuccess) {
        button.disabled = false
        if (isIconButton) {
          button.setAttribute("aria-label", originalText || "")
        } else {
          button.textContent = originalText
        }
      }
    }
  }

  formatElapsedTime(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    if (minutes > 0) return `${minutes}분 ${seconds}초`

    return `${seconds}초`
  }

  startFreshnessPolling() {
    if (!this.currentUpdatedAtValue || !this.freshnessUrlValue) return

    this.stopFreshnessPolling()
    this.pollTimer = window.setInterval(this.pollFreshness, 3000)
  }

  stopFreshnessPolling() {
    if (!this.pollTimer) return

    window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  handleAgentMessageComplete() {
    if (!this.currentUpdatedAtValue || this.hasExternalUpdate) return

    window.setTimeout(this.pollFreshness, 900)
  }

  async pollFreshness() {
    if (this.isPollingFreshness || this.hasExternalUpdate || document.visibilityState === "hidden") return

    this.isPollingFreshness = true

    try {
      const response = await fetch(this.freshnessUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        cache: "no-store"
      })

      if (!response.ok) return

      const payload = await response.json()
      const updatedAt = payload?.data?.updated_at || ""
      if (updatedAt && updatedAt !== this.currentUpdatedAtValue) {
        this.showFreshnessNotice()
      }
    } catch (_error) {
      // Freshness checks are advisory only; save/edit behavior should never depend on them.
    } finally {
      this.isPollingFreshness = false
    }
  }

  showFreshnessNotice() {
    this.hasExternalUpdate = true
    this.stopFreshnessPolling()

    if (this.hasFreshnessMessageTarget) {
      this.freshnessMessageTarget.textContent = "현재 입력 중인 내용은 그대로 두고, 새로고침할 때만 최신 초안을 불러옵니다."
    }

    if (this.hasFreshnessPanelTarget) {
      this.freshnessPanelTarget.classList.add("is-visible")
    }
  }

  startSectionObserver() {
    if (this.activeTabValue !== "service") return
    if (!this.hasNavLinkTarget || !("IntersectionObserver" in window)) return
    this.stopSectionObserver()

    const sections = Array.from(this.element.querySelectorAll("[data-ksrd-section][id]"))
    if (sections.length === 0) return

    this.sectionObserver = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
      if (!visible) return

      this.activateNavLink(visible.target.id)
    }, { rootMargin: "-18% 0px -62% 0px", threshold: [0.08, 0.24, 0.48] })

    sections.forEach((section) => this.sectionObserver.observe(section))
  }

  stopSectionObserver() {
    this.sectionObserver?.disconnect()
    this.sectionObserver = null
  }

  activateNavLink(sectionId) {
    this.navLinkTargets.forEach((link) => {
      link.classList.toggle("is-active", link.dataset.sectionId === sectionId)
    })
  }

  applyWorkspaceTab() {
    const active = this.activeTabValue === "profile" ? "profile" : "service"

    this.workspaceTabTargets.forEach((tab) => {
      const selected = tab.dataset.workspaceTab === active
      tab.classList.toggle("is-active", selected)
      tab.setAttribute("aria-selected", selected ? "true" : "false")
    })

    this.workspacePanelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.workspacePanel !== active
    })

    if (this.hasWorkspaceLayoutTarget) {
      this.workspaceLayoutTarget.classList.toggle("is-profile-tab", active === "profile")
      this.workspaceLayoutTarget.classList.remove("is-comparison-tab")
    }

    if (this.hasSectionNavTarget) {
      this.sectionNavTarget.hidden = active !== "service"
    }

    if (active === "service") {
      this.startSectionObserver()
    } else {
      this.stopSectionObserver()
    }

    this.element.dispatchEvent(new CustomEvent("kmong-service-registration:workspace-tab-change", {
      bubbles: true,
      detail: { tab: active }
    }))
  }

  fieldText(field) {
    if (field.dataset.contentFormat !== "html") return field.value || ""

    const template = document.createElement("template")
    template.innerHTML = field.value || ""
    return template.content.textContent || ""
  }
}
